/**
 * Canvas-based entry view: notes are positioned on an xy plane with (0,0) at center.
 * - Pan: drag the background.
 * - Shift + drag the background: marquee (rubber-band) select; intersecting cards are added to the multi-selection.
 * - Zoom: mouse wheel.
 * - Drag a note to reposition it.
 * - Click a note to select (menu / go); Enter to open.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, MenuItem, Snackbar, Theme, Tooltip, useMediaQuery } from '@mui/material';
import { GENERIC_ENTRY_VIEW_ADD_ENTRY_REPOSITORY_OPTIONS, REPOSITORIES_BY_ENTITY_TYPE } from '../constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { selectCurrentSubEntries, selectCurrentLocationEntriesMaxRank } from './repositoryEntryViewSlice';
import { useAddEntryForm } from '../components/AddEntryForm';
import { EditFormDialog, useEditForm } from '../components/EditEntryForm';
import ConfirmDeleteEntryDialog, { useConfirmDeleteEntryDialog } from '../components/ConfirmDeleteEntryDialog';
import ConfirmDeleteEntriesDialogV2 from '../components/ConfirmDeleteEntriesDialogV2';
import { assignRandomId, buildCollectionUrlWithFilters, extractEntryTextFieldUrls, extractUrlsFromText, getRepositorySingleUnitLabel, recursiveChildRecordIdsFind } from '../common/util';
import { useGenericListItemMenu } from '../components/GenericListItemMenu';
import { AnchorEffectAssignValueEntry, AnchorEntry, CanvasPosition, ChartEntry, GridEntry, QueryEntry, Entry, EntityType, FormData as FormDataType, PressedKey, PropertyDefinitionSelectStringOptionEntry, Repository, RepositoryEntry, ReferenceEntry, NoteEntry, TimelineCollectionEntry, TimelineEntry } from '../types';
import {
	addFormRepositoryOptionsSetState,
	selectIsAnyDialogOpen,
	confirmDeleteDialogEntrySetState,
} from './formDataSlice';
import { useKeyboardEffects } from '../components/KeyboardEffects';
import {
	RowMenuItem,
	RowMenuOptionEdit,
	RowMenuOptionGoTo,
	RowMenuOptionDelete,
	RowMenuOptionClip,
} from '../components/EntryMenu';
import { useContentNavigationEffects } from '../hooks/useContentNavigationEffects';
import { navigateRollOutward } from './navigationSlice';
import {
	entriesAddMany,
	entriesDeleteMany,
	entriesUpdateMany,
	selectAllAnchorEffects,
	selectAllTimelineCollections,
	selectAllEntries,
	selectAllEntryProperties,
	selectAllPropertyDefinitions,
	selectAllRepositories,
	selectAllRepositoryEntries,
	selectIsEntriesHydrated,
	selectLocationId,
} from './contentSlice';
import {
	useEntryCreateMutation,
	useEntryDeleteMutation,
	useLoadEntryQuery,
	useEntryUpdateMutation,
	useEntryRepositionMutation,
} from '../services/records';
import { useDeleteNoteMutation } from '../services/notes';
import { AiConversationWithMessages, AskAiMessage, useAskAiMutation } from '../services/ai';
import { selectIsAuthenticated } from './authSlice';
import AddNoteDialog from '../components/AddNoteDialog';
import AddQueryDialog from '../components/AddQueryDialog';
import EditQueryDialog from '../components/EditQueryDialog';
import EditNoteDialog from '../components/EditNoteDialog';
import AddChartDialog from '../components/AddChartDialog';
import EditChartDialog from '../components/EditChartDialog';
import AddAnchorDialog from '../components/AddAnchorDialog';
import EditGridDialog from '../components/EditGridDialog';
import EditAnchorDialog from '../components/EditAnchorDialog';
import AddTimelineDialog from '../components/AddTimelineDialog';
import EditTimelineDialog from '../components/EditTimelineDialog';
import { type TimelineCollectionInput } from '../components/TimelineCollectionsField';
import { AddRepositoryEntryFormDialog, useRepositoryEntryDialog } from '../components/AddRepositoryEntryDialog';
import { useCreateReferencesMutation, useLoadReferencesAtLocationQuery } from '../services/references';
import { useCreateNoteMutation, useLoadNotesAtLocationQuery, useUpdateNoteMutation } from '../services/notes';
import CanvasClickMarker from '../components/CanvasClickMarker';
import CanvasNoteCard from '../components/CanvasNoteCard';
import { useQueryCreateMutation } from '../services/queries';
import { useAnchorCreateMutation } from '../services/anchors';
import EntryMenu from '../components/EntryMenu';
import FabAreaWrapperBottomRight from '../components/FabAreaWrapperBottomRight';
import ClipboardActionsFab from '../components/ClipboardActionsFab';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { NOTE_REPOSITORY } from '../systemRecords/note';
import { REPOSITORY_REPOSITORY } from '../systemRecords/repository';
import { Link, useNavigate } from 'react-router-dom';
import CanvasScaleControl from '../components/CanvasScaleControl';
import { LockRipple, type LockRippleHandle } from '../components/LockRipple';
import { PositionLockButton } from '../components/PositionLockButton';
import { useGlassBarClass } from '../hooks/useGlassBarClass';
import { HeaderSlotContext } from '../components/HeaderWrapper';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { localStorageGetCanvasMeta, localStorageGetCanvasViewport, localStorageGetIsOfflineSyncPromptDismissed, localStorageSetCanvasMeta, localStorageSetCanvasViewport, localStorageSetIsOfflineSyncPromptDismissed, obtainLocalEntries } from '../localStorage';
import SyncOfflineDataDialog from '../components/SyncOfflineDataDialog';
import { SIGN_IN_TOOLTIP_TEXT } from '../components/AskAIBox';
import { ChatFlow } from '../components/ChatFlow';
import { mockAskAiResponse } from '../mocks/mock-ask-ai-response';
import AssistantIcon from '@mui/icons-material/Assistant';
import ReactGA from 'react-ga4';
import { CanvasQueryCard } from '../components/CanvasQueryCard';
import { CanvasChartCard } from '../components/CanvasChartCard';
import { CanvasGridCard } from '../components/CanvasGridCard';
import { CanvasAnchorCard } from '../components/CanvasAnchorCard';
import { CanvasTimelineCard } from '../components/CanvasTimelineCard';
import { useChartEntryUpdate } from '../hooks/useChartEntryUpdate';
import type { ChartPresentationVariant } from '../chart/canvasChartData';
import CanvasReferenceCard from '../components/CanvasReferenceCard';
import { RepositoryEntryDrawer } from './RepositoryEntryDrawer';
import { useRepositoryEntryData } from '../hooks/useRepositoryEntryData';
import { useRepositoryEntryEditFlow } from '../hooks/useRepositoryEntryEditFlow';
import { useRepositoryEntryDrawerChanges } from '../hooks/useRepositoryEntryDrawerChanges';
import { CanvasAddEntitySelection, isCanvasAddReferenceRepositorySelection } from '../components/canvasAddEntitySelection';
import CanvasAddElementSelectButton from '../components/CanvasAddElementSelectButton';
import { RepositoryEntriesRowDto, useLoadRepositoriesQuery } from '../services/repositoryEntries';
import useAuth from '../components/Auth';
import useCanvasPanZoom from '../hooks/useCanvasPanZoom';
import useCanvasMarqueeSelect from '../hooks/useCanvasMarqueeSelect';
import useCanvasGrid from '../hooks/useCanvasGrid';
import useCanvasAnchors from '../hooks/useCanvasAnchors';
import { resolveLinkedGroupIds } from '../utils/canvasLinkedGroupUtils';
import { REFERENCE_CARD_HOSTING_ANCHOR_Z_INDEX, REFERENCE_CARD_Z_INDEX } from '../utils/canvasZIndex';
import type { AnchorLineSpec, SnappableEntry } from '../utils/canvasAnchorUtils';
import { GRID_PRIMARY_AXIS_ITEM_SPACING } from '../utils/canvasGridUtils';
import usePopulateAnchorReferences from '../hooks/usePopulateAnchorReferences';
import { ActionAreaBtn, ActionAreaFab } from '../components/ActionAreaFab';
import { CanvasConnectorLine, CanvasConnection } from '../components/CanvasConnectorLine';
import { EntryExternalLinkActions } from '../components/EntryExternalLinkActions';

const DEFAULT_PLACEMENT_OFFSET = 24;
const MIN_SCALE = 0.25;
const MAX_SCALE = 2;
const USE_MOCK_AI_RESPONSE = process.env.REACT_APP_MOCK_AI_RESPONSE === 'true';
/** Hide the dot grid when zoomed out past this scale (avoids a noisy / overly dense pattern). */
const CANVAS_DOT_GRID_MIN_SCALE = 0.75;
/** Stable shared reference for "no attached anchor lines" so an unattached Reference card's props don't change identity every render. */
const EMPTY_ANCHOR_LINES: AnchorLineSpec[] = [];
/** Max distance (canvas units) for a Reference card to count as "nearby" a Note, for the pin-to-reference primary action. */
const NEARBY_REFERENCE_MAX_DISTANCE = GRID_PRIMARY_AXIS_ITEM_SPACING;
const CANVAS_VIEWPORT_SAVE_DEBOUNCE_MS = 300;
const PICKED_REFERENCES_ROW_SPACING = 200;

function defaultPositionForIndex(index: number): CanvasPosition {
	return {
		x: index * DEFAULT_PLACEMENT_OFFSET,
		y: index * DEFAULT_PLACEMENT_OFFSET,
	};
}

function offsetPlacement(position: CanvasPosition): CanvasPosition {
	return {
		x: position.x + DEFAULT_PLACEMENT_OFFSET,
		y: position.y + DEFAULT_PLACEMENT_OFFSET,
	};
}

/** Nearest Reference card to a Note, by canvas position. Null if there are no Reference cards. */
function findNearestReferenceEntry(
	noteId: string,
	entriesWithCanvasPositions: { entry: Entry; x: number; y: number }[],
): { id: string; distanceSq: number } | null {
	const notePosition = entriesWithCanvasPositions.find((row) => row.entry.id === noteId);
	if (!notePosition) return null;
	let nearestReferenceId: string | null = null;
	let nearestDistanceSq = Infinity;
	for (const row of entriesWithCanvasPositions) {
		if (row.entry.type !== EntityType.Reference) continue;
		const dx = row.x - notePosition.x;
		const dy = row.y - notePosition.y;
		const distanceSq = dx * dx + dy * dy;
		if (distanceSq < nearestDistanceSq) {
			nearestDistanceSq = distanceSq;
			nearestReferenceId = row.entry.id;
		}
	}
	return nearestReferenceId ? { id: nearestReferenceId, distanceSq: nearestDistanceSq } : null;
}


/** Todo: implement pan clamping. Farthest world coordinate (from center) that can be brought to the viewport edge. Same at every zoom. */

/**
 * Keyboard / navigation state model (shared with GenericEntryView via useContentNavigationEffects):
 *
 * State:
 * - focusId: id of the entry whose tabbable control currently has focus (or null)
 * - selectedEntryId: id of the "armed" entry that Enter will navigate into (or null)
 *
 * Scenario A: keyboard-only, 2-step navigation
 *   1. Tab → focus moves between canvas notes
 *        focusId = entry.id
 *        selectedEntryId = null
 *   2. First Enter on focused entry
 *        handleKeyPressEnter:
 *          - because selectedEntryId is null and focusId is set,
 *            it calls handleEntryClick(focusId)
 *          - useContentNavigationEffects sets selectedEntryId = focusId
 *        Result: note becomes "selected" (visual highlight), but we stay on this level.
 *   3. Second Enter on the same entry
 *        handleKeyPressEnter:
 *          - selectedEntryId is now set
 *          - dispatch(navigateRollInward({ focusId: selectedEntryId }))
 *        Result: navigate into the sub-entry.
 *
 * Scenario B: mouse click + keyboard
 *   1. Click on a note
 *        onSelect → handleEntryClick(entry.id)
 *        Result: selectedEntryId = entry.id (highlighted), focusId is also aligned by effects.
 *   2. Press Enter
 *        handleKeyPressEnter navigates inward using selectedEntryId.
 *   3. Press Escape
 *        handleKeyPressEscape dispatches navigateRollOutward(), leaving the current level
 *        (focus/selection are reset by the shared navigation effects on route change).
 *
 * Backspace / Space:
 * - Backspace: if focusId is set, opens the delete-confirm dialog for that entry.
 * - Space: if focusId is set, opens the edit dialog for that entry; otherwise, opens Add dialog.
 */

/** Data `buildCardHandlers`'s closures need that actually changes across renders — refreshed every CanvasView render via a ref (see `cardInteractionDepsRef`), never closed over directly. */
type CardInteractionDeps = {
	entryById: Map<string, Entry>;
	positionById: Map<string, CanvasPosition>;
	visibleEntries: Entry[];
	activeGridEntry: GridEntry | null;
	handlePositionChange: (entry: Entry, x: number, y: number) => void;
	syncAnchorAutoAssignFromPosition: (entry: Entry, x: number, y: number) => void;
	getConstrainedDragPosition: (entry: Entry, x: number, y: number) => CanvasPosition;
	constrainDragPositionForAnchors: (entry: Entry, x: number, y: number) => CanvasPosition;
	getAnchorDragTilt: (entry: Entry, x: number, y: number) => number;
	handleEntryFocus: (id: string) => void;
	handleEntryClick: (id: string) => void;
	handleEntryBlur: () => void;
	openListItemMenu: (entry: Entry) => void;
	getIdsLinkedToGridAtAnchor: (grid: GridEntry, anchorX: number, anchorY: number) => string[];
	expandSelectedIdsWithSwimlaneChildren: (selectedIds: string[]) => string[];
	swimlaneChildIdsByRootId: Map<string, string[]>;
	applyGridAxisPreviewDelta: (deltaX: number, deltaY: number) => void;
	applySwimlanePreviewDeltaForRoot: (rootId: string, deltaX: number, deltaY: number) => void;
};

/** The subset `buildCardHandlers` needs that's already reference-stable across renders (refs, setState setters, a zero-dep useCallback) — safe to close over directly. */
type CardInteractionStable = {
	getPosition: (entry: Entry, index: number) => CanvasPosition;
	setSelectedEntryIds: React.Dispatch<React.SetStateAction<string[]>>;
	setDraggingReferenceRepositoryId: React.Dispatch<React.SetStateAction<string | null>>;
	setIsDraggingNoteForAnchors: React.Dispatch<React.SetStateAction<boolean>>;
	setDragSnapMode: React.Dispatch<React.SetStateAction<'vertical' | 'horizontal' | null>>;
	setActiveSwimlaneCoord: React.Dispatch<React.SetStateAction<{ orientation: 'vertical' | 'horizontal'; coord: number } | null>>;
	dragSnapModeRef: React.MutableRefObject<'vertical' | 'horizontal' | null>;
	selectedEntryIdsRef: React.MutableRefObject<string[]>;
	groupDragStateRef: React.MutableRefObject<{
		draggedId: string;
		selectedIds: string[];
		startById: Map<string, CanvasPosition>;
		draggedStart: CanvasPosition;
	} | null>;
	cardNodeByIdRef: React.MutableRefObject<Map<string, HTMLDivElement>>;
	connectorLineNodeRef: React.MutableRefObject<HTMLDivElement | null>;
	connectorLineIdsRef: React.MutableRefObject<{ fromId: string; toId: string } | null>;
};

type CardHandlerBundle = {
	onPositionChange: (x: number, y: number) => void;
	constrainPosition: (x: number, y: number) => CanvasPosition;
	getDragTilt: (x: number, y: number) => number;
	onFocus: () => void;
	onBlur: () => void;
	onSelect: (e: React.PointerEvent<Element>) => void;
	onMenuClick: (e: React.MouseEvent) => void;
	onDragActivate: () => void;
	onDragEnd: () => void;
	onDragMove: (x: number, y: number) => void;
	cardNodeRef: (el: HTMLDivElement | null) => void;
};

/**
 * Builds the per-entry drag/select handler bundle passed to each canvas card. Panning/zooming re-renders
 * `CanvasView` on every animation frame, which used to rebuild this bundle (and its ~9 closures) fresh for
 * every visible entry on every one of those frames — the same problem fixed in `TimelineView`, but here it
 * also defeated any attempt at memoizing the card components, since a fresh closure every render always
 * looks like a changed prop. The caller builds this once per entry id and caches it (`cardHandlersCacheRef`
 * in `CanvasView`), so it stays referentially stable across renders even though pan/zoom/selection keep
 * happening — each closure reads current data through `depsRef.current` (refreshed every render) or the
 * already-stable refs/setters in `stable`, rather than closing over render-scoped variables.
 */
function buildCardHandlers(
	id: string,
	depsRef: React.RefObject<CardInteractionDeps>,
	stable: CardInteractionStable,
): CardHandlerBundle {
	return {
		onPositionChange: (x, y) => {
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			if (!entry) return;
			// `handlePositionChange` clears the group drag state, so it's captured first.
			const groupDragState = stable.groupDragStateRef.current;
			deps.handlePositionChange(entry, x, y);
			deps.syncAnchorAutoAssignFromPosition(entry, x, y);
			const isLeadingGroupDrag = !!groupDragState && groupDragState.draggedId === id;
			if (!isLeadingGroupDrag) return;
			// also apply the drop off effects to items that were part of the group
			const deltaX = x - groupDragState.draggedStart.x;
			const deltaY = y - groupDragState.draggedStart.y;
			for (const selectedId of groupDragState.selectedIds) {
				if (selectedId === id) continue;
				const targetEntry = deps.entryById.get(selectedId);
				const start = groupDragState.startById.get(selectedId);
				if (!targetEntry || !start) continue;
				const dropped = deps.getConstrainedDragPosition(targetEntry, start.x + deltaX, start.y + deltaY);
				deps.syncAnchorAutoAssignFromPosition(targetEntry, dropped.x, dropped.y);
			}
		},
		constrainPosition: (x, y) => {
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			if (!entry) return { x, y };
			const afterGrid = deps.getConstrainedDragPosition(entry, x, y);
			return deps.constrainDragPositionForAnchors(entry, afterGrid.x, afterGrid.y);
		},
		getDragTilt: (x, y) => {
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			if (!entry) return 0;
			return deps.getAnchorDragTilt(entry, x, y);
		},
		onFocus: () => depsRef.current.handleEntryFocus(id),
		onBlur: () => {
			/** TODO: unselect entry after tabbing into the add button. Currently the problem is though that using handleEntryBlur will break stop the navigation from happening on enter key  */
		},
		onSelect: (e) => {
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			if (!entry) return;
			const isGridOrAnchor = entry.type === EntityType.Grid || entry.type === EntityType.Anchor;
			if (e.shiftKey && stable.selectedEntryIdsRef.current.includes(id)) {
				const removedIds = isGridOrAnchor ? [id] : resolveLinkedGroupIds([id], deps.visibleEntries);
				stable.setSelectedEntryIds((prev) => prev.filter((sid) => !removedIds.includes(sid)));
				deps.handleEntryBlur();
				return;
			}
			if (isGridOrAnchor) {
				if (e.shiftKey) {
					stable.setSelectedEntryIds((prev) => [...prev, id]);
				} else {
					stable.setSelectedEntryIds([id]);
				}
			} else if (e.shiftKey) {
				stable.setSelectedEntryIds((prev) => resolveLinkedGroupIds([...prev, id], deps.visibleEntries));
			} else {
				stable.setSelectedEntryIds(resolveLinkedGroupIds([id], deps.visibleEntries));
			}
			deps.handleEntryClick(id);
		},
		onMenuClick: (e) => {
			e.stopPropagation();
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			if (entry) deps.openListItemMenu(entry);
		},
		onDragActivate: () => {
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			if (!entry) return;
			deps.handleEntryClick(id);
			const previousGroup = stable.groupDragStateRef.current;
			if (previousGroup) {
				for (const selectedId of previousGroup.selectedIds) {
					if (selectedId === previousGroup.draggedId) continue;
					const node = stable.cardNodeByIdRef.current.get(selectedId);
					if (node) node.style.transform = '';
				}
			}
			const latestSelectedIds = stable.selectedEntryIdsRef.current;
			let selectedIds: string[] = [];
			if (entry.type === EntityType.Grid) {
				const position = deps.positionById.get(id) ?? { x: 0, y: 0 };
				const linked = deps.getIdsLinkedToGridAtAnchor(entry as GridEntry, position.x, position.y);
				selectedIds = Array.from(new Set([id, ...linked]));
			} else if (entry.type === EntityType.Anchor) {
				selectedIds = resolveLinkedGroupIds([id], deps.visibleEntries);
			} else if (latestSelectedIds.includes(id)) {
				selectedIds = deps.expandSelectedIdsWithSwimlaneChildren(latestSelectedIds);
			} else {
				selectedIds = deps.expandSelectedIdsWithSwimlaneChildren([id]);
			}
			// Recursively expand: any selected anchor or reference pulls in all transitively linked entries.
			selectedIds = resolveLinkedGroupIds(selectedIds, deps.visibleEntries);
			if (
				selectedIds.length !== latestSelectedIds.length
				|| selectedIds.some((sid) => !latestSelectedIds.includes(sid))
			) {
				stable.setSelectedEntryIds(selectedIds);
			}
			const startById = new Map<string, CanvasPosition>();
			for (const selectedId of selectedIds) {
				const selectedIndex = deps.visibleEntries.findIndex((e) => e.id === selectedId);
				if (selectedIndex < 0) {
					continue;
				}
				const selectedEntry = deps.visibleEntries[selectedIndex];
				startById.set(selectedId, stable.getPosition(selectedEntry, selectedIndex));
			}
			const draggedPosition = deps.positionById.get(id) ?? { x: 0, y: 0 };
			stable.groupDragStateRef.current = {
				draggedId: id,
				selectedIds,
				startById,
				draggedStart: startById.get(id) ?? draggedPosition,
			};
			if (entry.type === EntityType.Reference) {
				stable.setDraggingReferenceRepositoryId((entry as ReferenceEntry).repositoryId);
				stable.setIsDraggingNoteForAnchors(false);
				stable.dragSnapModeRef.current = null;
				stable.setDragSnapMode(null);
				stable.setActiveSwimlaneCoord(null);
			} else {
				stable.setDraggingReferenceRepositoryId(null);
				stable.setIsDraggingNoteForAnchors(entry.type === EntityType.Note);
				stable.dragSnapModeRef.current = null;
				stable.setDragSnapMode(null);
				stable.setActiveSwimlaneCoord(null);
			}
		},
		onDragEnd: () => {
			const deps = depsRef.current;
			const entry = deps.entryById.get(id);
			const groupDragState = stable.groupDragStateRef.current;
			const swimlanePreviewRootIds =
				entry?.type === EntityType.Reference
					? Array.from(
						new Set([
							id,
							...(groupDragState?.selectedIds.filter((sid) => deps.swimlaneChildIdsByRootId.has(sid)) ?? []),
						]),
					)
					: [];
			if (groupDragState) {
				for (const selectedId of groupDragState.selectedIds) {
					if (selectedId === groupDragState.draggedId) continue;
					const node = stable.cardNodeByIdRef.current.get(selectedId);
					if (node) node.style.transform = '';
				}
			}
			stable.groupDragStateRef.current = null;
			deps.applyGridAxisPreviewDelta(0, 0);
			for (const rootId of swimlanePreviewRootIds) {
				deps.applySwimlanePreviewDeltaForRoot(rootId, 0, 0);
			}
			stable.setDraggingReferenceRepositoryId(null);
			stable.setIsDraggingNoteForAnchors(false);
			stable.dragSnapModeRef.current = null;
			stable.setDragSnapMode(null);
			stable.setActiveSwimlaneCoord(null);
			if (entry?.type === EntityType.Grid || entry?.type === EntityType.Anchor) {
				stable.setSelectedEntryIds([]);
			}
		},
		onDragMove: (x, y) => {
			const deps = depsRef.current;
			const groupDragState = stable.groupDragStateRef.current;
			if (!groupDragState || groupDragState.draggedId !== id) {
				return;
			}
			const entry = deps.entryById.get(id);
			if (!entry) return;
			const deltaX = x - groupDragState.draggedStart.x;
			const deltaY = y - groupDragState.draggedStart.y;
			if (deps.activeGridEntry?.id === id) {
				deps.applyGridAxisPreviewDelta(deltaX, deltaY);
			}
			if (entry.type === EntityType.Reference) {
				const swimlanePreviewRootIds = Array.from(
					new Set([
						id,
						...groupDragState.selectedIds.filter((sid) => deps.swimlaneChildIdsByRootId.has(sid)),
					]),
				);
				for (const rootId of swimlanePreviewRootIds) {
					deps.applySwimlanePreviewDeltaForRoot(rootId, deltaX, deltaY);
				}
			}
			groupDragState.selectedIds
				.filter((sid) => sid !== id)
				.forEach((sid) => {
					const targetEntry = deps.visibleEntries.find((e) => e.id === sid);
					const start = groupDragState.startById.get(sid);
					if (!targetEntry || !start) {
						return;
					}
					const constrained = deps.getConstrainedDragPosition(targetEntry, start.x + deltaX, start.y + deltaY);
					const node = stable.cardNodeByIdRef.current.get(targetEntry.id);
					if (node) {
						const targetAnchorId = (targetEntry as Partial<SnappableEntry>).anchorId;
						const isMovingWithItsAnchorLine = !!targetAnchorId && groupDragState.selectedIds.includes(targetAnchorId);
						const tilt = isMovingWithItsAnchorLine
							? 0
							: deps.getAnchorDragTilt(targetEntry, constrained.x, constrained.y);
						node.style.transform = `translate(calc(-50% + ${constrained.x}px), calc(-50% + ${constrained.y}px)) rotate(${tilt}deg)`;
					}
				});
			// Keep the connector line tracking live while either of its endpoints is being dragged, instead of
			// waiting for the drag to commit and CanvasView to re-render (see `CanvasConnectorLine`).
			const connection = stable.connectorLineIdsRef.current;
			if (connection && stable.connectorLineNodeRef.current) {
				const resolveLivePosition = (targetId: string): CanvasPosition | undefined => {
					if (targetId === id) return { x, y };
					const start = groupDragState.startById.get(targetId);
					const targetEntry = deps.visibleEntries.find((e) => e.id === targetId);
					if (!start || !targetEntry) return deps.positionById.get(targetId);
					return deps.getConstrainedDragPosition(targetEntry, start.x + deltaX, start.y + deltaY);
				};
				const fromPosition = resolveLivePosition(connection.fromId);
				const toPosition = resolveLivePosition(connection.toId);
				if (fromPosition && toPosition) {
					const lineDx = toPosition.x - fromPosition.x;
					const lineDy = toPosition.y - fromPosition.y;
					const distance = Math.sqrt(lineDx * lineDx + lineDy * lineDy);
					const angle = Math.atan2(lineDy, lineDx) * (180 / Math.PI);
					stable.connectorLineNodeRef.current.style.width = `${distance}px`;
					stable.connectorLineNodeRef.current.style.transform = `translate(${fromPosition.x}px, ${fromPosition.y}px) rotate(${angle}deg)`;
				}
			}
		},
		cardNodeRef: (el) => {
			if (el) {
				stable.cardNodeByIdRef.current.set(id, el);
			} else {
				stable.cardNodeByIdRef.current.delete(id);
			}
		},
	};
}

function CanvasView() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const containerRef = useRef<HTMLDivElement>(null);
	const locationSubEntries = useAppSelector(selectCurrentSubEntries);
	const allRepositories = useAppSelector(selectAllRepositories);
	const visibleEntries = useMemo(
		() =>
			(locationSubEntries as Entry[]).filter((e) =>
				[EntityType.Note, EntityType.Reference, EntityType.Query, EntityType.Chart, EntityType.Grid, EntityType.Anchor, EntityType.Timeline].includes(e.type)
			),
		[locationSubEntries]
	);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allAnchorEffects = useAppSelector(selectAllAnchorEffects);
	const allTimelineCollections = useAppSelector(selectAllTimelineCollections);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const entriesWithCanvasPositions = useMemo(
		() =>
			(visibleEntries as Entry[]).map((entry, index) => {
				const p = (entry as any).xy ?? defaultPositionForIndex(index);
				return { entry, x: p.x, y: p.y };
			}),
		[visibleEntries]
	);
	/** O(1) id lookups for the card-handler factory below, instead of `.find`-ing `visibleEntries` per card per render. */
	const entryById = useMemo(() => {
		const map = new Map<string, Entry>();
		for (const e of visibleEntries as Entry[]) map.set(e.id, e);
		return map;
	}, [visibleEntries]);
	const positionById = useMemo(() => {
		const map = new Map<string, CanvasPosition>();
		for (const row of entriesWithCanvasPositions) map.set(row.entry.id, { x: row.x, y: row.y });
		return map;
	}, [entriesWithCanvasPositions]);
	/** Repository ids that already have Reference cards on this canvas (location). */
	const currentLocationRelevantRepositoryIds = useMemo(() => {
		const ids = new Set<string>();
		for (const e of visibleEntries as Entry[]) {
			if (e.type === EntityType.Reference) {
				ids.add((e as ReferenceEntry).repositoryId);
			}
		}
		return ids;
	}, [visibleEntries]);
	const parentId = useAppSelector(selectLocationId);
	const allEntries = useAppSelector(selectAllEntries);
	const { isAuthenticating } = useAuth();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const { data: remoteRepositories = [], isSuccess: isRemoteRepositoriesLoaded } = useLoadRepositoriesQuery(undefined, {
		skip: !isAuthenticated,
	});
	const {
		currentData: loadedLocationRecords,
		isLoading: isLoadEntryInProgress,
		isError: isLoadEntryError,
	} = useLoadEntryQuery(parentId ?? null, {
		skip: isAuthenticating || !isAuthenticated,
	});
	const isContentLoaded = !isLoadEntryInProgress;
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const isWorkspaceReady = isAuthenticated ? isContentLoaded : isEntriesHydrated;
	const { isAddDialogOpen, openAddNoteDialog, closeAddDialog } = useAddEntryForm();
	const repositoryEntryDialogControl = useRepositoryEntryDialog();
	const {
		isAddRepositoryEntryDialogOpen,
		addRepositoryEntryDialog,
		openAddRepositoryEntryDialog,
	} = repositoryEntryDialogControl;
	const repositoryEntryDrawerChanges = useRepositoryEntryDrawerChanges();
	const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen) || repositoryEntryDrawerChanges.isUnsavedChangesDialogOpen;
	const { isEditDialogOpen, editFormEntry, openEditDialog, closeEditDialog, onEditFormSubmit } = useEditForm();
	const { focusIdRef, focusId, selectedEntryId, handleEntryClick, handleEntryFocus, handleEntryBlur } =
		useContentNavigationEffects();
	const {
		isConfirmDeleteDialogOpen,
		confirmDeleteDialogEntry,
		closeConfirmDeleteDialog,
		submitConfirmDeleteDialog,
	} = useConfirmDeleteEntryDialog();
	const {
		menuAnchorEl,
		setMenuAnchorEl,
		menuEntry,
		openListItemMenu,
		closeListItemMenu,
		handleMenuCutBtnClick,
	} = useGenericListItemMenu({ entries: visibleEntries as Entry[] });
	const {
		pan,
		scale,
		panStart,
		setPan,
		setViewport,
		handleScaleControlChange,
		handleCanvasPointerDown: handlePanZoomPointerDown,
		handleCanvasPointerMove: handlePanZoomPointerMove,
		handleCanvasPointerUp: handlePanZoomPointerUp,
		consumeTapCancelled,
		getWorldFromClientPoint,
	} = useCanvasPanZoom({
		containerRef,
		minScale: MIN_SCALE,
		maxScale: MAX_SCALE,
		containerReady: isContentLoaded,
	});
	const [markerWorld, setMarkerWorld] = useState<CanvasPosition | null>(null);
	const [markerPlacementGeneration, setMarkerPlacementGeneration] = useState(0);
	const [entryCreateMutation] = useEntryCreateMutation();
	const [createNoteMutation] = useCreateNoteMutation();
	const [updateNoteMutation] = useUpdateNoteMutation();
	const [referenceCreateMutation] = useCreateReferencesMutation();
	const [queryCreateMutation] = useQueryCreateMutation();
	const [anchorCreateMutation] = useAnchorCreateMutation();
	const [entryDeleteMutation] = useEntryDeleteMutation();
	const [deleteNoteMutation] = useDeleteNoteMutation();
	const [entryUpdateMutation] = useEntryUpdateMutation();
	const [entryRepositionMutation] = useEntryRepositionMutation();
	const currentMaxRank = useAppSelector(selectCurrentLocationEntriesMaxRank);
	const [repositoryEntryDrawer, setRepositoryEntryDrawer] = useState<{
		repositoryId: string;
		repositoryEntryId: string;
		referenceEntryId: string;
	} | null>(null);
	const repositoryEntryDrawerOpen = Boolean(repositoryEntryDrawer);
	const { requestEntryChange: requestRepositoryEntryChange } = repositoryEntryDrawerChanges;
	const requestRepositoryEntryDrawer = useCallback((next: typeof repositoryEntryDrawer) => {
		requestRepositoryEntryChange(next?.repositoryEntryId ?? null, () => setRepositoryEntryDrawer(next));
	}, [requestRepositoryEntryChange]);
	const repositoryEntryDocumentData = useRepositoryEntryData(
		repositoryEntryDrawerOpen,
		repositoryEntryDrawer?.repositoryEntryId ?? null,
	);
	const repositoryEntryCanvasEditFlow = useRepositoryEntryEditFlow({
		activeEntry: repositoryEntryDrawerOpen ? repositoryEntryDocumentData.populatedEntry : null,
		repositoryEntryDialog: repositoryEntryDialogControl,
		onNavigateToEntry: (entryId) => navigate(`/entries/${entryId}`),
		resetKey: repositoryEntryDrawer?.repositoryEntryId ?? '',
		isAnyDialogOpen,
		drawerChanges: repositoryEntryDrawerChanges,
	});
	// reload edit entry drawer content when selected entry changes
	useEffect(() => {
		if (repositoryEntryDrawer && selectedEntryId !== repositoryEntryDrawer?.repositoryEntryId) {
			const selectedEntry = visibleEntries.find((entry) => entry.id === selectedEntryId);
			if (selectedEntry?.type !== EntityType.Reference) {
				requestRepositoryEntryDrawer(null);
				return;
			}
			const ref = selectedEntry as ReferenceEntry;
			requestRepositoryEntryDrawer({ repositoryId: ref.repositoryId, repositoryEntryId: ref.repositoryEntryId, referenceEntryId: ref.id });
		}
	}, [selectedEntryId, repositoryEntryDrawer?.repositoryEntryId]);
	// reset edit entry drawer and restore the location's saved pan and zoom when location changes
	useEffect(() => {
		requestRepositoryEntryDrawer(null);
		setViewport(localStorageGetCanvasViewport(parentId) ?? { pan: { x: 0, y: 0 }, scale: 1 });
	}, [parentId, setViewport]);
	const latestViewportRef = useRef({ parentId, viewport: { pan, scale } });
	useEffect(() => {
		latestViewportRef.current = { parentId, viewport: { pan, scale } };
		const timeoutId = setTimeout(() => localStorageSetCanvasViewport(parentId, { pan, scale }), CANVAS_VIEWPORT_SAVE_DEBOUNCE_MS);
		return () => clearTimeout(timeoutId);
	}, [parentId, pan, scale]);
	useEffect(() => () => {
		const latest = latestViewportRef.current;
		localStorageSetCanvasViewport(latest.parentId, latest.viewport);
	}, []);
	const setKeyboardMappings = useKeyboardEffects();
	const [isChatWindowOpen, setIsChatWindowOpen] = useState(false);
	const addQueryRepositories = useMemo<Repository[]>(
		() =>
			(isRemoteRepositoriesLoaded ? remoteRepositories : allRepositories).filter(
				(repository) => ![NOTE_REPOSITORY.id, REPOSITORY_REPOSITORY.id].includes(repository.id)
			),
		[isRemoteRepositoriesLoaded, remoteRepositories, allRepositories]
	);
	const addRepositoryOptions = useMemo<PropertyDefinitionSelectStringOptionEntry[]>(
		() =>
			addQueryRepositories.map((repository) => ({
				id: `idCanvasRepositoryOption${repository.id}`,
				type: EntityType.PropertyDefinitionSelectStringOption,
				parentId: 'SYSTEM',
				label: repository.title,
				value: repository.id,
			})),
		[addQueryRepositories]
	);
	const repositoryColorsById = useMemo(
		() =>
			addQueryRepositories.reduce((acc, repository) => {
				if (repository.color) {
					acc.set(repository.id, repository.color);
				}
				return acc;
			}, new Map<string, string>()),
		[addQueryRepositories]
	);
	const drawerRefEntryId = repositoryEntryDrawer?.referenceEntryId ?? null;
	/** All anchors currently attached to this reference — a reference can now host more than one. */
	const drawerAttachedAnchors = useMemo(() => {
		if (!drawerRefEntryId) return [];
		return (visibleEntries as Entry[]).filter(
			(e) => e.type === EntityType.Anchor && (e as AnchorEntry).referenceId === drawerRefEntryId,
		) as AnchorEntry[];
	}, [drawerRefEntryId, visibleEntries]);
	const drawerAnchorTargetRepositoryTitleByAnchorId = useMemo(() => {
		const map = new Map<string, string | null>();
		for (const anchor of drawerAttachedAnchors) {
			map.set(anchor.id, anchor.repositoryId ? (addQueryRepositories.find((r) => r.id === anchor.repositoryId)?.title ?? null) : null);
		}
		return map;
	}, [drawerAttachedAnchors, addQueryRepositories]);

	/** The field that records what a pinnable entry is pinned to: Notes/References point at an anchor via `anchorId`; an Anchor points at the reference it's attached to via `referenceId`. */
	const pinFieldForEntryType = (type: EntityType) => (type === EntityType.Anchor ? 'referenceId' : 'anchorId');

	const handleUnpinEntry = useCallback((entry: Entry) => {
		const payload = [{ id: entry.id, type: entry.type, [pinFieldForEntryType(entry.type)]: null }] as any[];

		const referenceId = entry.type === EntityType.Anchor ? (entry as AnchorEntry).referenceId : null;
		if (referenceId) {
			// The anchor is about to snap back from the reference's position to its own stored xy — shift
			// entries attached to its line by the same delta so they don't get left behind at the old spot.
			const referencePosition = positionById.get(referenceId);
			const anchorOwnPosition = positionById.get(entry.id);
			if (referencePosition && anchorOwnPosition) {
				const dx = anchorOwnPosition.x - referencePosition.x;
				const dy = anchorOwnPosition.y - referencePosition.y;
				if (dx !== 0 || dy !== 0) {
					for (const attachedEntry of visibleEntries as Entry[]) {
						if ((attachedEntry as ReferenceEntry | NoteEntry).anchorId !== entry.id) continue;
						const attachedPosition = positionById.get(attachedEntry.id);
						if (!attachedPosition) continue;
						payload.push({
							id: attachedEntry.id,
							type: attachedEntry.type,
							xy: { x: attachedPosition.x + dx, y: attachedPosition.y + dy },
						});
					}
				}
			}
		}

		if (isAuthenticated) entryRepositionMutation(payload);
		else dispatch(entriesUpdateMany(payload));
		closeListItemMenu();
	}, [positionById, visibleEntries, isAuthenticated, entryRepositionMutation, dispatch, closeListItemMenu]);

	const handlePinEntryToNearestReference = useCallback((entry: Entry) => {
		const nearestReference = findNearestReferenceEntry(entry.id, entriesWithCanvasPositions);
		if (!nearestReference) return;
		const payload = [{ id: entry.id, type: entry.type, [pinFieldForEntryType(entry.type)]: nearestReference.id }] as any[];

		if (entry.type === EntityType.Anchor) {
			// An attached anchor's displayed position is inherited from its reference, not its own xy — so
			// entries already snapped onto its line need to move by the same delta or they'll be left behind.
			const anchorOldPosition = positionById.get(entry.id);
			const referencePosition = positionById.get(nearestReference.id);
			if (anchorOldPosition && referencePosition) {
				const dx = referencePosition.x - anchorOldPosition.x;
				const dy = referencePosition.y - anchorOldPosition.y;
				if (dx !== 0 || dy !== 0) {
					for (const attachedEntry of visibleEntries as Entry[]) {
						if ((attachedEntry as ReferenceEntry | NoteEntry).anchorId !== entry.id) continue;
						const attachedPosition = positionById.get(attachedEntry.id);
						if (!attachedPosition) continue;
						payload.push({
							id: attachedEntry.id,
							type: attachedEntry.type,
							xy: { x: attachedPosition.x + dx, y: attachedPosition.y + dy },
						});
					}
				}
			}
		}

		if (isAuthenticated) entryRepositionMutation(payload);
		else dispatch(entriesUpdateMany(payload));
		closeListItemMenu();
	}, [entriesWithCanvasPositions, positionById, visibleEntries, isAuthenticated, entryRepositionMutation, dispatch, closeListItemMenu]);

	/** Whether the selected Note/Anchor should show the primary pin/unpin action: already pinned, or has a Reference card nearby to pin to. */
	const selectedPinAction = useMemo(() => {
		if (!selectedEntryId) return null;
		const entry = entryById.get(selectedEntryId);
		if (!entry) return null;
		if (entry.type === EntityType.Note) {
			if ((entry as NoteEntry).anchorId) return { entry, isPinned: true as const };
		} else if (entry.type === EntityType.Anchor) {
			if ((entry as AnchorEntry).referenceId) return { entry, isPinned: true as const };
		} else {
			return null;
		}
		const nearestReference = findNearestReferenceEntry(entry.id, entriesWithCanvasPositions);
		if (!nearestReference || nearestReference.distanceSq > NEARBY_REFERENCE_MAX_DISTANCE ** 2) return null;
		return { entry, isPinned: false as const };
	}, [selectedEntryId, entryById, entriesWithCanvasPositions]);

	const [addEntitySelection, setAddEntitySelection] = useState<string>(CanvasAddEntitySelection.Note);
	const canvasAddToolkitPinnedRepositoryIds = useMemo(() => {
		const ids = new Set<string>(currentLocationRelevantRepositoryIds);
		if (isCanvasAddReferenceRepositorySelection(addEntitySelection)) {
			ids.add(addEntitySelection);
		}
		return Array.from(ids);
	}, [currentLocationRelevantRepositoryIds, addEntitySelection]);
	const [isAddQueryDialogOpen, setIsAddQueryDialogOpen] = useState(false);
	const [editNoteEntry, setEditNoteEntry] = useState<NoteEntry | null>(null);
	const [editQueryEntry, setEditQueryEntry] = useState<QueryEntry | null>(null);
	const [isAddChartDialogOpen, setIsAddChartDialogOpen] = useState(false);
	const [isAddGridDialogOpen, setIsAddGridDialogOpen] = useState(false);
	const [editGridEntry, setEditGridEntry] = useState<GridEntry | null>(null);
	const [editAnchorEntry, setEditAnchorEntry] = useState<AnchorEntry | null>(null);
	const [isAddTimelineDialogOpen, setIsAddTimelineDialogOpen] = useState(false);
	const [editTimelineEntry, setEditTimelineEntry] = useState<TimelineEntry | null>(null);
	const [editChartEntry, setEditChartEntry] = useState<ChartEntry | null>(null);
	const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
	const [isSyncDismissedSnackbarOpen, setIsSyncDismissedSnackbarOpen] = useState(false);
	const [localRecordsToSync, setLocalRecordsToSync] = useState<Entry[]>([]);
	const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);

	/**
	 * Connector line endpoints, shown while a pinned Note is part of the current selection (single, multi-select, or
	 * focus). `CanvasConnectorLine`/`CanvasConnection` are generic (any pair of canvas entries) — this is just today's
	 * only caller, scoped to a Note pinned to a Reference card.
	 */
	const selectedPinConnection = useMemo((): CanvasConnection | null => {
		const candidateIds = Array.from(new Set([...selectedEntryIds, selectedEntryId, focusId].filter((id): id is string => Boolean(id))));
		for (const id of candidateIds) {
			const entry = entryById.get(id);
			if (entry?.type !== EntityType.Note) continue;
			const anchorId = (entry as NoteEntry).anchorId;
			if (!anchorId) continue;
			// A Note's anchorId may point at an AnchorEntry (grid-line pin) instead of a Reference; only draw for the latter.
			if (entryById.get(anchorId)?.type !== EntityType.Reference) continue;
			const fromPosition = positionById.get(entry.id);
			const toPosition = positionById.get(anchorId);
			if (!fromPosition || !toPosition) continue;
			return {
				fromId: entry.id,
				toId: anchorId,
				fromX: fromPosition.x,
				fromY: fromPosition.y,
				toX: toPosition.x,
				toY: toPosition.y,
			};
		}
		return null;
	}, [selectedEntryIds, selectedEntryId, focusId, entryById, positionById]);
	useEffect(() => {
		connectorLineIdsRef.current = selectedPinConnection
			? { fromId: selectedPinConnection.fromId, toId: selectedPinConnection.toId }
			: null;
	}, [selectedPinConnection]);

	/** When set, bulk-delete confirmation (V2) is open for these entry ids (length ≥ 2). */
	const [pendingBulkDeleteIds, setPendingBulkDeleteIds] = useState<string[] | null>(null);
	const [draggingReferenceRepositoryId, setDraggingReferenceRepositoryId] = useState<string | null>(null);
	/** Notes aren't restricted to one collection, so while dragging one every anchor line is a candidate. */
	const [isDraggingNoteForAnchors, setIsDraggingNoteForAnchors] = useState(false);
	const [dragSnapMode, setDragSnapMode] = useState<'vertical' | 'horizontal' | null>(null);
	const dragSnapModeRef = useRef<'vertical' | 'horizontal' | null>(null);
	const [activeSwimlaneCoord, setActiveSwimlaneCoord] = useState<{ orientation: 'vertical' | 'horizontal'; coord: number } | null>(null);
	const [chatDraft, setChatDraft] = useState('');
	const [chatMessages, setChatMessages] = useState<AskAiMessage[]>([]);
	const [chatConversationId, setChatConversationId] = useState<string | null>(null);
	const [isAiSearchFocused, setIsAiSearchFocused] = useState(false);
	const [askAiMutation, { isLoading: isAskAiInProgress }] = useAskAiMutation();
	const [isAskAIBoxExpanded, setIsAskAIBoxExpanded] = useState(false);
	const [isWelcomeDismissed, setIsWelcomeDismissed] = useState(false);
	const isWelcomeMode = isWorkspaceReady && allEntries.length === 0 && !isWelcomeDismissed;
	const isAskAIBoxOpen = isAskAIBoxExpanded || isWelcomeMode;
	const showAskAIBox = useCallback(() => {
		setIsAskAIBoxExpanded(true);
		setIsWelcomeDismissed(false);
	}, []);
	const hideAskAIBox = useCallback(() => {
		setIsAskAIBoxExpanded(false);
		setIsWelcomeDismissed(true);
	}, []);
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const glassBarClass = useGlassBarClass();
	const headerSlotEl = useContext(HeaderSlotContext);
	const [isPositionLocked, setIsPositionLocked] = useState(true);
	const isLocationLoaded = isAuthenticated ? Boolean(loadedLocationRecords) : isEntriesHydrated;
	// Resolved once per location load, so placing the first item on an empty board doesn't lock it mid-session.
	useEffect(() => {
		if (!isLocationLoaded) return;
		setIsPositionLocked(localStorageGetCanvasMeta(parentId).isPositionLocked ?? visibleEntries.length > 0);
	}, [parentId, isLocationLoaded]);
	const lockRippleRef = useRef<LockRippleHandle>(null);
	const togglePositionLock = useCallback(() => {
		const next = !isPositionLocked;
		setIsPositionLocked(next);
		localStorageSetCanvasMeta(parentId, { isPositionLocked: next });
		lockRippleRef.current?.play();
	}, [isPositionLocked, parentId]);
	const selectedEntryIdsSet = useMemo(() => new Set(selectedEntryIds), [selectedEntryIds]);
	const selectedEntryIdsRef = useRef<string[]>([]);
	const groupDragStateRef = useRef<{
		draggedId: string;
		selectedIds: string[];
		startById: Map<string, CanvasPosition>;
		draggedStart: CanvasPosition;
	} | null>(null);
	const cardNodeByIdRef = useRef<Map<string, HTMLDivElement>>(new Map());
	const verticalAxisNodeRef = useRef<HTMLDivElement | null>(null);
	const horizontalAxisNodeRef = useRef<HTMLDivElement | null>(null);
	const connectorLineNodeRef = useRef<HTMLDivElement | null>(null);
	const connectorLineIdsRef = useRef<{ fromId: string; toId: string } | null>(null);
	const projectedVerticalLaneNodeByCoordRef = useRef<Map<string, HTMLDivElement>>(new Map());
	const projectedHorizontalLaneNodeByCoordRef = useRef<Map<string, HTMLDivElement>>(new Map());

	useEffect(() => {
		if (isLoadEntryError) {
			navigate('/');
		}
	}, [isLoadEntryError, navigate]);
	useEffect(() => {
		selectedEntryIdsRef.current = selectedEntryIds;
	}, [selectedEntryIds]);
	const getPosition = useCallback(
		(entry: Entry, index: number): CanvasPosition => {
			if ((entry as any).xy) {
				return (entry as any).xy as CanvasPosition;
			}
			return defaultPositionForIndex(index);
		},
		[]
	);
	const {
		anchorAxisLines,
		constrainDragPositionForAnchors,
		getAnchorDragTilt,
		syncAnchorAutoAssignFromPosition,
	} = useCanvasAnchors({
		visibleEntries: visibleEntries as Entry[],
		entriesWithCanvasPositions,
		allPropertyDefinitions,
		anchorEffects: allAnchorEffects,
		isAuthenticated,
		draggingReferenceRepositoryId,
		isDraggingNote: isDraggingNoteForAnchors,
		getPosition,
	});

	const {
		activeGridEntry,
		activeGridAxisSnap,
		gridSnapX,
		gridSnapY,
		gridAxisBounds,
		projectedSwimlaneVertical,
		projectedSwimlaneHorizontal,
		isDraggingYAxisReference,
		isDraggingXAxisReference,
		isDraggingCollectionReference,
		swimlaneChildIdsByRootId,
		getIdsLinkedToGridAtAnchor,
		expandSelectedIdsWithSwimlaneChildren,
		applyGridAxisPreviewDelta,
		applySwimlanePreviewDeltaForRoot,
		getConstrainedDragPosition,
		handlePositionChange,
	} = useCanvasGrid({
		visibleEntries: visibleEntries as Entry[],
		entriesWithCanvasPositions,
		allPropertyDefinitions,
		isAuthenticated,
		draggingReferenceRepositoryId,
		dragSnapMode,
		activeSwimlaneCoord,
		setActiveSwimlaneCoord,
		dragSnapModeRef,
		setDragSnapMode,
		selectedEntryIdsRef,
		groupDragStateRef,
		getPosition,
		verticalAxisNodeRef,
		horizontalAxisNodeRef,
		projectedVerticalLaneNodeByCoordRef,
		projectedHorizontalLaneNodeByCoordRef,
		anchorAxisLines,
	});

	/**
	 * `anchorAxisLines` is re-scanned with `.find`/`.filter` once per matching card in the render loop
	 * below — harmless at typical counts, but it's an O(n) scan per card on every render (including every
	 * pan/zoom frame), so it's indexed once here instead. `anchorLineByAnchorId` keeps first-match semantics
	 * to match the `.find` it replaces; `anchorLinesByReferenceId` groups the rest by `referenceId`.
	 */
	const anchorLineByAnchorId = useMemo(() => {
		const map = new Map<string, AnchorLineSpec>();
		for (const line of anchorAxisLines) {
			if (!map.has(line.anchorId)) map.set(line.anchorId, line);
		}
		return map;
	}, [anchorAxisLines]);
	const anchorLinesByReferenceId = useMemo(() => {
		const map = new Map<string, AnchorLineSpec[]>();
		for (const line of anchorAxisLines) {
			if (!line.referenceId) continue;
			const list = map.get(line.referenceId);
			if (list) list.push(line);
			else map.set(line.referenceId, [line]);
		}
		return map;
	}, [anchorAxisLines]);

	const { populateAnchorReferences } = usePopulateAnchorReferences({
		allRepositoryEntries,
		allEntryProperties,
		parentId: parentId ?? null,
		isAuthenticated,
	});

	const {
		marqueeDisplayRect,
		isMarqueeActive,
		consumeMarqueeSuppressedClick,
		beginMarqueePointerDown,
		handleMarqueePointerMove,
		handleMarqueePointerUp,
	} = useCanvasMarqueeSelect({
		containerRef,
		visibleEntries: visibleEntries as Entry[],
		cardNodeByIdRef,
		expandSelectedIdsWithSwimlaneChildren,
		setSelectedEntryIds,
		handleEntryClick,
	});

	/**
	 * - Drag on empty space: pans the canvas.
	 * - Shift-drag on empty space: draws a selection box.
	 * - Drag on an unlocked card: moves the card; the canvas stays put.
	 * - Drag on a locked card: pans the canvas, same as empty space.
	 * - Pinch with two fingers on empty space or locked cards: zooms the canvas.
	 * - Tap on any card: selects that card, and does not count as a tap on empty space.
	 */
	const handlePointerDown = useCallback(
		(e: React.PointerEvent) => {
			const targetIsCanvasCard = (e.target as HTMLElement).closest('[data-canvas-card]');
			if (e.shiftKey && e.button === 0 && !targetIsCanvasCard) {
				if (beginMarqueePointerDown(e)) return;
			}
			handlePanZoomPointerDown(e);
		},
		[beginMarqueePointerDown, handlePanZoomPointerDown],
	);

	const handlePointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (handleMarqueePointerMove(e)) return;
			handlePanZoomPointerMove(e);
		},
		[handleMarqueePointerMove, handlePanZoomPointerMove],
	);

	const handlePointerUp = useCallback(
		(e: React.PointerEvent) => {
			handleMarqueePointerUp(e);
			handlePanZoomPointerUp(e);
		},
		[handleMarqueePointerUp, handlePanZoomPointerUp],
	);

	useEffect(() => {
		if (!isAuthenticated || localStorageGetIsOfflineSyncPromptDismissed()) {
			setLocalRecordsToSync([]);
			return;
		}
		const localRecords = obtainLocalEntries();
		setLocalRecordsToSync(Array.isArray(localRecords) ? localRecords : []);
	}, [isAuthenticated]);

	const pickerRepositoryId = addRepositoryEntryDialog?.repositoryId;
	const isPickerEntryNotOnCanvas = useMemo(() => {
		const onCanvasIds = new Set<string>();
		(visibleEntries as Entry[]).forEach((e) => {
			if (e.type !== EntityType.Reference) return;
			const r = e as ReferenceEntry;
			if (r.repositoryId === pickerRepositoryId) onCanvasIds.add(r.repositoryEntryId);
		});
		return (row: RepositoryEntriesRowDto) => !onCanvasIds.has(row.id);
	}, [visibleEntries, pickerRepositoryId]);

	/** 
	 * This state is being used to prevent CanvasReferenceCards from fetching References data via individual API calls before the bundle response has a chance to pre-cache the data on their behalf.  
	 * Pre-loading the data in a bundle per each location prevents individual API calls from CanvasReferenceCards from flooding the API. 
	 * The individual queries on CanvasReferenceCards remain active only for when new items are added or when their cache is being invalidated in another way. 
	 */
	const {
		isFetching: isReferencesAtLocationBundleLoadFetching,
	} = useLoadReferencesAtLocationQuery(parentId ?? '', {
		skip: !isAuthenticated || !isContentLoaded,
	});
	const {
		isFetching: isNotesAtLocationBundleLoadFetching,
	} = useLoadNotesAtLocationQuery(parentId ?? '', {
		skip: !isAuthenticated || !isContentLoaded,
	});

	const closeCanvasAddFlowDialogs = useCallback(() => {
		closeAddDialog();
		repositoryEntryCanvasEditFlow.closeAddRepositoryEntryDialogWithCleanup();
		setIsAddQueryDialogOpen(false);
		setIsAddChartDialogOpen(false);
		setIsAddGridDialogOpen(false);
		setIsAddTimelineDialogOpen(false);
	}, [closeAddDialog, repositoryEntryCanvasEditFlow.closeAddRepositoryEntryDialogWithCleanup]);

	const openCanvasAddFlowForSelection = useCallback(
		(selection: string) => {
			if (selection === CanvasAddEntitySelection.Note) {
				openAddNoteDialog();
				return;
			}
			if (selection === CanvasAddEntitySelection.Query) {
				setIsAddQueryDialogOpen(true);
				return;
			}
			if (selection === CanvasAddEntitySelection.Chart) {
				setIsAddChartDialogOpen(true);
				return;
			}
			if (selection === CanvasAddEntitySelection.Grid) {
				setIsAddGridDialogOpen(true);
				return;
			}
			if (selection === CanvasAddEntitySelection.Timeline) {
				setIsAddTimelineDialogOpen(true);
				return;
			}
			if (isCanvasAddReferenceRepositorySelection(selection)) {
				openAddRepositoryEntryDialog({ repositoryId: selection });
				return;
			}
		},
		[openAddNoteDialog, openAddRepositoryEntryDialog]
	);

	const handleCanvasAddEntitySelectionChange = useCallback(
		(nextValue: string) => {
			if (nextValue === addEntitySelection) {
				return;
			}
			closeCanvasAddFlowDialogs();
			setAddEntitySelection(nextValue);
			openCanvasAddFlowForSelection(nextValue);
		},
		[addEntitySelection, closeCanvasAddFlowDialogs, openCanvasAddFlowForSelection]
	);

	const canvasAddElementSelectSlot = useMemo(
		() => (
			<CanvasAddElementSelectButton
				value={addEntitySelection}
				onChange={handleCanvasAddEntitySelectionChange}
				showQueryChartGrid={addQueryRepositories.length > 0}
				referenceOptions={addRepositoryOptions.map((o) => ({
					value: String(o.value),
					label: String(o.label ?? ''),
					color: repositoryColorsById.get(String(o.value)) ?? 'primary.main',
				}))}
				pinnedRepositoryIds={canvasAddToolkitPinnedRepositoryIds}
			/>
		),
		[
			addEntitySelection,
			handleCanvasAddEntitySelectionChange,
			addQueryRepositories,
			addRepositoryOptions,
			repositoryColorsById,
			canvasAddToolkitPinnedRepositoryIds,
		],
	);

	const openSelectedAddDialog = useCallback(() => {
		openCanvasAddFlowForSelection(addEntitySelection);
	}, [addEntitySelection, openCanvasAddFlowForSelection]);

	const handleCanvasClick = useCallback(
		(e: React.MouseEvent) => {
			if (consumeMarqueeSuppressedClick()) return;
			if (consumeTapCancelled()) return;
			handleEntryBlur();
			setSelectedEntryIds([]);
			const point = getWorldFromClientPoint(e.clientX, e.clientY);
			if (!point) return;
			setMarkerWorld(point);
			setMarkerPlacementGeneration((g) => g + 1);
		},
		[consumeMarqueeSuppressedClick, consumeTapCancelled, getWorldFromClientPoint, handleEntryBlur]
	);

	const editDialogTitle = useMemo(
		() =>
			editFormEntry
				? `Edit ${getRepositorySingleUnitLabel(REPOSITORIES_BY_ENTITY_TYPE[editFormEntry.type]).toLowerCase()}`
				: '',
		[editFormEntry]
	);
	const confirmDeleteDialogTitle = useMemo(
		() =>
			confirmDeleteDialogEntry
				? `Delete ${getRepositorySingleUnitLabel(REPOSITORIES_BY_ENTITY_TYPE[confirmDeleteDialogEntry.type]).toLowerCase()}?`
				: '',
		[confirmDeleteDialogEntry]
	);

	const handleCanvasMenuGoToClick = useCallback(
		(item: RowMenuItem) => {
			closeListItemMenu();
			const entry = item as Entry;
			if (entry.type === EntityType.Reference) {
				const ref = entry as ReferenceEntry;
				requestRepositoryEntryDrawer({ repositoryId: ref.repositoryId, repositoryEntryId: ref.repositoryEntryId, referenceEntryId: ref.id });
				return;
			}
			if (entry.type === EntityType.Query) {
				navigate(`/collection/${(entry as QueryEntry).repositoryId}`);
				return;
			}
			if (entry.type === EntityType.Chart) {
				navigate(`/chart/${entry.id}`);
				return;
			}
			if (entry.type === EntityType.Timeline) {
				navigate(`/timeline/${entry.id}`);
				return;
			}
			navigate(`/${entry.id}`);
		},
		[closeListItemMenu, navigate],
	);

	const handleCanvasMenuCutClick = useCallback(() => {
		if (!menuEntry) return;
		const ids =
			selectedEntryIds.length > 0 && selectedEntryIds.includes(menuEntry.id)
				? Array.from(new Set(selectedEntryIds))
				: [menuEntry.id];
		handleMenuCutBtnClick(ids);
	}, [handleMenuCutBtnClick, menuEntry, selectedEntryIds]);

	const handleMenuEditBtnClick = (id: string) => {
		closeListItemMenu();
		const entry = visibleEntries.find((entry) => entry.id === id);
		if (!entry) return;
		if (entry.type === EntityType.Reference) {
			const ref = entry as ReferenceEntry;
			requestRepositoryEntryDrawer({ repositoryId: ref.repositoryId, repositoryEntryId: ref.repositoryEntryId, referenceEntryId: ref.id });
			return;
		}
		if (entry.type === EntityType.Query) {
			setEditQueryEntry(entry as QueryEntry);
			return;
		}
		if (entry.type === EntityType.Chart) {
			setEditChartEntry(entry as ChartEntry);
			return;
		}
		if (entry.type === EntityType.Grid) {
			setEditGridEntry(entry as GridEntry);
			return;
		}
		if (entry.type === EntityType.Anchor) {
			setEditAnchorEntry(entry as AnchorEntry);
			return;
		}
		if (entry.type === EntityType.Timeline) {
			setEditTimelineEntry(entry as TimelineEntry);
			return;
		}
		if (entry.type === EntityType.Note) {
			setEditNoteEntry(entry as NoteEntry);
			return;
		}
		openEditDialog(entry);
	};

	const handleReferencePickerConfirm = useCallback(
		(selectedRepositoryEntryIds: string[]) => {
			const origin = markerWorld ?? defaultPositionForIndex(visibleEntries.length);
			const columns = Math.ceil(Math.sqrt(selectedRepositoryEntryIds.length));
			const payloads = selectedRepositoryEntryIds.map((repositoryEntryId, idx) =>
				assignRandomId({
					type: EntityType.Reference,
					parentId: parentId ?? null,
					repositoryId: pickerRepositoryId,
					repositoryEntryId,
					xy: {
						x: origin.x + (idx % columns) * GRID_PRIMARY_AXIS_ITEM_SPACING,
						y: origin.y + Math.floor(idx / columns) * PICKED_REFERENCES_ROW_SPACING,
					},
				})
			);
			if (isAuthenticated) {
				referenceCreateMutation(payloads as ReferenceEntry[]);
			} else {
				dispatch(entriesAddMany(payloads as any));
			}
			setMarkerWorld(offsetPlacement(origin));
		},
		[
			pickerRepositoryId,
			markerWorld,
			parentId,
			visibleEntries.length,
			isAuthenticated,
			referenceCreateMutation,
			dispatch,
		]
	);

	const isEditQueryDialogOpen = Boolean(editQueryEntry);
	const isEditNoteDialogOpen = Boolean(editNoteEntry);
	const isEditGridDialogOpen = Boolean(editGridEntry);
	const isEditAnchorDialogOpen = Boolean(editAnchorEntry);
	const isEditTimelineDialogOpen = Boolean(editTimelineEntry);
	const isEditChartDialogOpen = Boolean(editChartEntry);
	const isCanvasAddAuxiliaryDialogOpen =
		isAddQueryDialogOpen
		|| isEditQueryDialogOpen
		|| isEditNoteDialogOpen
		|| isAddChartDialogOpen
		|| isAddGridDialogOpen
		|| isEditGridDialogOpen
		|| isEditAnchorDialogOpen
		|| isAddTimelineDialogOpen
		|| isEditTimelineDialogOpen
		|| isEditChartDialogOpen
		|| Boolean(repositoryEntryDrawer);

	const handleConfirmBulkDelete = useCallback(() => {
		const ids = pendingBulkDeleteIds;
		if (!ids?.length) return;
		const idSet = new Set<string>();
		for (const id of ids) {
			idSet.add(id);
			for (const childId of recursiveChildRecordIdsFind(id, allEntries)) {
				idSet.add(childId);
			}
		}
		// Cascade: include Anchors bound to any Reference being deleted
		const deletingReferenceIds = new Set(
			Array.from(idSet).filter((id) => allEntries.find((e) => e.id === id)?.type === EntityType.Reference),
		);
		if (deletingReferenceIds.size > 0) {
			for (const entry of allEntries) {
				if (entry.type === EntityType.Anchor && deletingReferenceIds.has((entry as AnchorEntry).referenceId ?? '')) {
					idSet.add(entry.id);
				}
			}
		}
		const mergedIds = Array.from(idSet);
		const entriesToDelete = mergedIds
			.map((id) => allEntries.find((e) => e.id === id))
			.filter((e): e is Entry => Boolean(e));
		// The API deletes a timeline's collection rows along with it, so they're only dropped from the store here.
		const timelineCollectionIds = allTimelineCollections
			.filter((collection) => idSet.has(collection.timelineId))
			.map((collection) => collection.id);
		if (isAuthenticated) {
			for (const entry of entriesToDelete) {
				if (entry.type === EntityType.Note) {
					deleteNoteMutation({ id: entry.id, parentId: entry.parentId ?? '' });
				} else {
					entryDeleteMutation({ id: entry.id });
				}
			}
			dispatch(entriesDeleteMany(timelineCollectionIds));
		} else {
			dispatch(entriesDeleteMany([...mergedIds, ...timelineCollectionIds]));
		}
		setPendingBulkDeleteIds(null);
		setSelectedEntryIds([]);
		handleEntryBlur();
	}, [
		allEntries,
		deleteNoteMutation,
		allTimelineCollections,
		dispatch,
		entryDeleteMutation,
		handleEntryBlur,
		isAuthenticated,
		pendingBulkDeleteIds,
	]);

	const handleKeyPressEscape = useCallback(() => {
		if (pendingBulkDeleteIds?.length) {
			setPendingBulkDeleteIds(null);
			return;
		}
		setMarkerWorld(null);
		if (repositoryEntryDrawer && (repositoryEntryCanvasEditFlow.hasFieldOrRelationDialog || repositoryEntryDrawerChanges.isUnsavedChangesDialogOpen)) {
			return;
		}
		if (repositoryEntryDrawer) {
			requestRepositoryEntryDrawer(null);
			return;
		}
		if (isCanvasAddAuxiliaryDialogOpen) {
			// Escape is handled by the dialog (submits selection); avoid navigating away.
			return;
		}
		if (isAnyDialogOpen) return;
		if (focusId) {
			handleEntryBlur();
		} else {
			dispatch(navigateRollOutward());
		}
	}, [
		dispatch,
		focusId,
		handleEntryBlur,
		isAnyDialogOpen,
		isCanvasAddAuxiliaryDialogOpen,
		pendingBulkDeleteIds,
		repositoryEntryDrawer,
		repositoryEntryCanvasEditFlow.hasFieldOrRelationDialog,
		repositoryEntryDrawerChanges.isUnsavedChangesDialogOpen,
		requestRepositoryEntryDrawer,
	]);

	const selectedEntryUrl = useMemo(() => {
		if (!selectedEntryId) {
			return null;
		}
		const selectedEntry = (visibleEntries as Entry[]).find((e) => e.id === selectedEntryId);
		if (!selectedEntry) {
			return null;
		}
		switch (selectedEntry.type) {
		case EntityType.Reference:
			return `/entries/${(selectedEntry as ReferenceEntry).repositoryEntryId}`;
		case EntityType.Query:
			return buildCollectionUrlWithFilters(
				(selectedEntry as QueryEntry).repositoryId,
				(selectedEntry as QueryEntry).where,
				(selectedEntry as QueryEntry).includeArchived,
			);
		case EntityType.Chart:
			return `/chart/${selectedEntry.id}`;
		case EntityType.Timeline:
			return `/timeline/${selectedEntry.id}`;
		}
		return `/${selectedEntryId}`;
	}, [selectedEntryId, visibleEntries]);

	const selectedReferenceEntryId = useMemo(() => {
		const selectedEntry = (visibleEntries as Entry[]).find((e) => e.id === selectedEntryId);
		return selectedEntry?.type === EntityType.Reference ? (selectedEntry as ReferenceEntry).repositoryEntryId : null;
	}, [selectedEntryId, visibleEntries]);
	const selectedReferenceEntryData = useRepositoryEntryData(Boolean(selectedReferenceEntryId), selectedReferenceEntryId);
	const selectedEntryExternalUrls = useMemo(() => {
		const selectedEntry = (visibleEntries as Entry[]).find((e) => e.id === selectedEntryId);
		if (selectedEntry?.type === EntityType.Note) {
			const note = selectedEntry as NoteEntry;
			return Array.from(new Set(extractUrlsFromText(`${note.title}`)));
		}
		return extractEntryTextFieldUrls(selectedReferenceEntryData.propertyDefinitions, selectedReferenceEntryData.populatedEntry?.data);
	}, [selectedEntryId, visibleEntries, selectedReferenceEntryData.propertyDefinitions, selectedReferenceEntryData.populatedEntry]);

	const handleKeyPressEnter = useCallback(
		(event?: KeyboardEvent) => {
			if (pendingBulkDeleteIds?.length) {
				return;
			}
			if (isCanvasAddAuxiliaryDialogOpen) {
				return;
			}
			if (isAnyDialogOpen || repositoryEntryDrawerOpen) {
				return;
			}
			if (repositoryEntryCanvasEditFlow.hasFieldOrRelationDialog) {
				return;
			}
			if (selectedEntryUrl) {
				event?.preventDefault(); // prevent the default link behavior after having clicked on the repository list item (Link)
				navigate(selectedEntryUrl);
				handleEntryBlur();
				return;
			}
			if (focusIdRef.current) {
				event?.preventDefault();
				event?.stopPropagation();
				handleEntryClick(focusIdRef.current);
			}
		},
		[
			selectedEntryUrl,
			selectedEntryId,
			visibleEntries,
			isAnyDialogOpen,
			repositoryEntryDrawerOpen,
			isCanvasAddAuxiliaryDialogOpen,
			pendingBulkDeleteIds,
			repositoryEntryCanvasEditFlow.hasFieldOrRelationDialog,
			handleEntryClick,
			navigate,
			handleEntryBlur,
		]
	);

	const handleKeyPressBackspace = useCallback(() => {
		if (repositoryEntryDrawer) return;
		if (pendingBulkDeleteIds?.length) return;
		if (!focusId || isAnyDialogOpen || isCanvasAddAuxiliaryDialogOpen) return;
		const multiIds =
			selectedEntryIds.length > 1 && selectedEntryIds.includes(focusId)
				? Array.from(new Set(selectedEntryIds))
				: null;
		if (multiIds && multiIds.length > 1) {
			setPendingBulkDeleteIds(multiIds);
			dispatch(confirmDeleteDialogEntrySetState(null));
			return;
		}
		const entry = (visibleEntries as Entry[]).find((e) => e.id === focusId);
		if (entry) {
			dispatch(confirmDeleteDialogEntrySetState(entry));
		}
	}, [
		dispatch,
		visibleEntries,
		focusId,
		isAnyDialogOpen,
		isCanvasAddAuxiliaryDialogOpen,
		repositoryEntryDrawer,
		selectedEntryIds,
		pendingBulkDeleteIds,
	]);

	const handleKeyPressSpace = useCallback((event?: KeyboardEvent) => {
		if (isAiSearchFocused) return;
		if (repositoryEntryDrawer) return;
		if (pendingBulkDeleteIds?.length) return;
		let handled = false;
		if (focusId && !isAnyDialogOpen && !isCanvasAddAuxiliaryDialogOpen) {
			const entry = (visibleEntries as Entry[]).find((e) => e.id === focusId);
			if (entry?.type === EntityType.Query) {
				setEditQueryEntry(entry as QueryEntry);
				handled = true;
			} else if (entry?.type === EntityType.Grid) {
				setEditGridEntry(entry as GridEntry);
				handled = true;
			} else if (entry?.type === EntityType.Anchor) {
				setEditAnchorEntry(entry as AnchorEntry);
				handled = true;
			} else if (entry?.type === EntityType.Timeline) {
				setEditTimelineEntry(entry as TimelineEntry);
				handled = true;
			} else if (entry?.type === EntityType.Chart) {
				setEditChartEntry(entry as ChartEntry);
				handled = true;
			} else if (entry?.type === EntityType.Reference) {
				const ref = entry as ReferenceEntry;
				requestRepositoryEntryDrawer({ repositoryId: ref.repositoryId, repositoryEntryId: ref.repositoryEntryId, referenceEntryId: ref.id });
				handled = true;
			} else if (entry?.type === EntityType.Note) {
				setEditNoteEntry(entry as NoteEntry);
				handled = true;
			} else if (entry) {
				openEditDialog(entry);
				handled = true;
			}
		} else if (!isAnyDialogOpen && !isCanvasAddAuxiliaryDialogOpen) {
			openSelectedAddDialog();
			handled = true;
		}
		if (handled) {
			event?.preventDefault();
			event?.stopPropagation();
		}
	}, [visibleEntries, openEditDialog, isAnyDialogOpen, openSelectedAddDialog, focusId, isCanvasAddAuxiliaryDialogOpen, isAiSearchFocused, repositoryEntryDrawer, pendingBulkDeleteIds]);

	useEffect(() => {
		dispatch(addFormRepositoryOptionsSetState(GENERIC_ENTRY_VIEW_ADD_ENTRY_REPOSITORY_OPTIONS));
	}, [dispatch]);

	// After creating a repository entry from the canvas picker, attach a Reference row at the marker or next free slot.
	const handleRepositoryEntryCreatedFromCanvas = useCallback(
		(repositoryEntryCreatedResult: RepositoryEntry) => {
			const xy = markerWorld ?? defaultPositionForIndex(visibleEntries.length);
			const referencePayload = assignRandomId({
				type: EntityType.Reference,
				parentId: parentId ?? null,
				repositoryId: repositoryEntryCreatedResult.repositoryId,
				repositoryEntryId: repositoryEntryCreatedResult.id,
				xy,
			});
			if (isAuthenticated) {
				referenceCreateMutation([referencePayload as ReferenceEntry]);
			} else {
				dispatch(entriesAddMany([referencePayload as any]));
			}
			setMarkerWorld(offsetPlacement(xy));
		},
		[markerWorld, visibleEntries.length, parentId, isAuthenticated, referenceCreateMutation, dispatch],
	);

	const clampPanToBounds = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;
		// Clamping isn't implemented yet (see the TODO above) — returning the same object (rather than an
		// `{x, y}` copy) lets React bail out instead of re-rendering the whole canvas for a no-op update.
		setPan((p) => p);
	}, [scale]);

	useEffect(() => {
		clampPanToBounds();
	}, [scale, clampPanToBounds]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(clampPanToBounds);
		ro.observe(el);
		return () => ro.disconnect();
	}, [clampPanToBounds]);

	useEffect(() => {
		setKeyboardMappings({
			[PressedKey.Escape]: handleKeyPressEscape,
			[PressedKey.Backspace]: handleKeyPressBackspace,
			[PressedKey.Enter]: handleKeyPressEnter,
			[PressedKey.Space]: handleKeyPressSpace,
		});
		return () => setKeyboardMappings(null);
	}, [handleKeyPressEscape, handleKeyPressBackspace, handleKeyPressEnter, handleKeyPressSpace, setKeyboardMappings]);

	const handleLocalRecordsSynced = useCallback(() => {
		setLocalRecordsToSync([]);
		setIsSyncDialogOpen(false);
	}, []);

	const handleDismissLocalRecordsSync = useCallback(() => {
		localStorageSetIsOfflineSyncPromptDismissed(true);
		setLocalRecordsToSync([]);
		setIsSyncDialogOpen(false);
		setIsSyncDismissedSnackbarOpen(true);
	}, []);

	const askAi = useCallback(async (query: string, history: AskAiMessage[], conversationId: string | null) => {
		const conversation: AskAiMessage[] = [...history, { role: 'user', content: query }];
		setChatMessages(conversation);
		setIsChatWindowOpen(true);
		let answer: string;
		if (USE_MOCK_AI_RESPONSE) {
			answer = mockAskAiResponse.answer ?? '';
		} else {
			try {
				const response = await askAiMutation({
					query,
					locationId: parentId ?? null,
					conversationId,
				}).unwrap();
				answer = response.answer ?? '';
				setChatConversationId(response.conversationId);
			} catch (error) {
				console.error('AI ask failed', error);
				answer = 'Unable to fetch AI response right now.';
			}
		}
		setChatMessages([...conversation, { role: 'assistant', content: answer }]);
	}, [askAiMutation, parentId]);

	const handleChatSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const query = chatDraft.trim();
		if (!query) return;
		setChatDraft('');
		if (isChatWindowOpen) {
			askAi(query, chatMessages, chatConversationId);
			return;
		}
		setChatConversationId(null);
		askAi(query, [], null);
	}, [chatDraft, askAi, isChatWindowOpen, chatMessages, chatConversationId]);

	const handleOpenPastConversation = useCallback((conversation: AiConversationWithMessages) => {
		setChatConversationId(conversation.id);
		setChatMessages(conversation.messages.map(({ role, content }) => ({ role, content })));
		setIsChatWindowOpen(true);
	}, []);

	const handleAddEntryClick = useCallback(() => {
		openSelectedAddDialog();
		ReactGA.event({
			category: 'UI',
			action: 'AddEntryClicked',
		});
	}, [openSelectedAddDialog]);

	const handleCanvasMenuDeleteClick = () => {
		closeListItemMenu();
		if (!menuEntry) return;
		const idsForBulk =
			selectedEntryIds.length > 1 && selectedEntryIds.includes(menuEntry.id)
				? Array.from(new Set(selectedEntryIds))
				: null;
		if (idsForBulk && idsForBulk.length > 1) {
			setPendingBulkDeleteIds(idsForBulk);
			dispatch(confirmDeleteDialogEntrySetState(null));
			return;
		}
		const entry = visibleEntries.find((e) => e.id === menuEntry.id);
		if (entry) {
			dispatch(confirmDeleteDialogEntrySetState(entry as Entry));
		}
	};

	const handleAddCanvasQueryDialogClose = useCallback(() => {
		setIsAddQueryDialogOpen(false);
	}, []);

	const handleAddCanvasQueryDialogSubmit = useCallback(
		({ repositoryId, title, where, includeArchived }: {
			repositoryId: string;
			title: string;
			where?: string;
			includeArchived: boolean;
		}) => {
			const xy = markerWorld ?? defaultPositionForIndex(visibleEntries.length);
			const payload = assignRandomId({
				type: EntityType.Query,
				parentId: parentId ?? null,
				repositoryId,
				rank: currentMaxRank + 1,
				title,
				where,
				includeArchived,
				xy,
			});
			if (isAuthenticated) {
				queryCreateMutation(payload as QueryEntry);
			} else {
				dispatch(entriesAddMany([payload as any]));
			}
			setMarkerWorld(offsetPlacement(xy));
			setIsAddQueryDialogOpen(false);
		},
		[currentMaxRank, dispatch, isAuthenticated, markerWorld, parentId, queryCreateMutation, visibleEntries.length]
	);

	const handleEditQueryDialogClose = useCallback(() => {
		setEditQueryEntry(null);
	}, []);

	const handleEditNoteDialogClose = useCallback(() => {
		setEditNoteEntry(null);
	}, []);

	const handleEditNoteDialogSave = useCallback(
		async (payload: { id: string; title: string; variant: NoteEntry['variant']; color: string | null }) => {
			const { id, ...patch } = payload;
			if (isAuthenticated) {
				await updateNoteMutation({ id, patch }).unwrap();
			} else {
				dispatch(entriesUpdateMany([{ id, ...patch }]));
			}
		},
		[dispatch, isAuthenticated, updateNoteMutation],
	);

	const handleEditNoteDialogSaveAndClose = useCallback(
		async (payload: { id: string; title: string; variant: NoteEntry['variant']; color: string | null }) => {
			await handleEditNoteDialogSave(payload);
			setEditNoteEntry(null);
		},
		[handleEditNoteDialogSave],
	);

	const handleEditQueryDialogSubmit = useCallback(
		({ id, repositoryId, title, where, includeArchived }: {
			id: string;
			repositoryId: string;
			title: string;
			where?: string;
			includeArchived: boolean;
		}) => {
			const updatePayload = {
				id,
				repositoryId,
				title,
				where,
				includeArchived,
			};
			if (isAuthenticated) {
				entryUpdateMutation([updatePayload as Partial<Entry>]);
			} else {
				dispatch(entriesUpdateMany([updatePayload as Partial<Entry>]));
			}
			setEditQueryEntry(null);
		},
		[dispatch, entryUpdateMutation, isAuthenticated]
	);

	const handleAddCanvasChartDialogClose = useCallback(() => {
		setIsAddChartDialogOpen(false);
	}, []);

	const handleEditGridDialogClose = useCallback(() => {
		setEditGridEntry(null);
	}, []);

	const handleEditGridDialogSubmit = useCallback(
		({ id, repositoryId, primaryPropertyId, secondaryPropertyId, direction }: {
			id: string;
			repositoryId?: string;
			primaryPropertyId?: string | null;
			secondaryPropertyId?: string | null;
			direction?: string | null;
		}) => {
			const updatePayload = {
				id,
				repositoryId: repositoryId ?? null,
				primaryPropertyId: primaryPropertyId ?? null,
				secondaryPropertyId: secondaryPropertyId ?? null,
				direction: direction ?? null,
			};
			if (isAuthenticated) {
				entryUpdateMutation([updatePayload as Partial<Entry>]);
			} else {
				dispatch(entriesUpdateMany([updatePayload as Partial<Entry>]));
			}
			setEditGridEntry(null);
		},
		[dispatch, entryUpdateMutation, isAuthenticated]
	);

	const handleAddCanvasGridDialogClose = useCallback(() => {
		setIsAddGridDialogOpen(false);
	}, []);

	const handleAddCanvasGridDialogSubmit = useCallback(
		async ({ repositoryId, axisRepositoryId, direction, autoAssignPropertyId, autoAssignPropertySlug, autoPopulateReferences }: {
			repositoryId: string;
			axisRepositoryId: string;
			direction: string;
			autoAssignPropertyId: string;
			autoAssignPropertySlug: string;
			autoPopulateReferences: boolean;
		}) => {
			const xy = markerWorld ?? defaultPositionForIndex(visibleEntries.length);

			// 1. Main anchor — auto-populates the group reference cards
			const mainAnchorLocal = assignRandomId({
				type: EntityType.Anchor,
				parentId: parentId ?? null,
				repositoryId: axisRepositoryId,
				referenceId: null,
				direction,
				xy,
			}) as AnchorEntry;
			const mainAnchor = isAuthenticated
				? await anchorCreateMutation(mainAnchorLocal).unwrap()
				: (() => { dispatch(entriesAddMany([mainAnchorLocal])); return mainAnchorLocal; })();

			if (autoAssignPropertyId) {
				// 2. Populate group references, then attach a linked anchor to each
				const groupRefs = await populateAnchorReferences(mainAnchor, xy);
				const oppositeDirection = direction === 'vertical' ? 'horizontal' : 'vertical';
				const linkedAnchorsLocal = groupRefs.map((ref) =>
					assignRandomId({
						type: EntityType.Anchor,
						parentId: parentId ?? null,
						repositoryId,
						referenceId: ref.id,
						direction: oppositeDirection,
						xy,
					}) as AnchorEntry
				);
				const linkedAnchors = linkedAnchorsLocal.length
					? isAuthenticated
						? await Promise.all(linkedAnchorsLocal.map((a) => anchorCreateMutation(a).unwrap()))
						: (() => { dispatch(entriesAddMany(linkedAnchorsLocal)); return linkedAnchorsLocal; })()
					: [];
				// Each linked anchor gets a hook that assigns its own group reference's entry id to the axis property.
				const effectEntries = linkedAnchors
					.map((anchor, i) => ({ anchor, groupRef: groupRefs[i] }))
					.filter(({ groupRef }) => Boolean(groupRef.repositoryEntryId))
					.map(({ anchor, groupRef }) => assignRandomId({
						type: EntityType.AnchorEffectAssignValue,
						parentId: parentId ?? null,
						anchorId: anchor.id,
						repositoryId,
						propertyId: autoAssignPropertyId,
						value: groupRef.repositoryEntryId as string,
					}) as AnchorEffectAssignValueEntry);
				if (effectEntries.length) {
					if (isAuthenticated) entryCreateMutation(effectEntries);
					else dispatch(entriesAddMany(effectEntries));
				}
				// 3. Populate collection entries onto each linked anchor, filtered by the group property value
				if (autoPopulateReferences && autoAssignPropertySlug) {
					await Promise.all(
						linkedAnchors.map((anchor, i) => {
							const groupRef = groupRefs[i];
							if (!groupRef.repositoryEntryId) return Promise.resolve([]);
							const refPosition = groupRef.xy ?? xy;
							return populateAnchorReferences(anchor, refPosition, {
								slug: autoAssignPropertySlug,
								targetEntryId: groupRef.repositoryEntryId,
							});
						}),
					);
				}
			} else if (autoPopulateReferences) {
				// No group by — populate entries from the collection directly onto the main anchor
				await populateAnchorReferences(mainAnchor, xy);
			}

			setMarkerWorld(offsetPlacement(xy));
			setIsAddGridDialogOpen(false);
		},
		[anchorCreateMutation, dispatch, entryCreateMutation, isAuthenticated, markerWorld, parentId, populateAnchorReferences, visibleEntries.length]
	);

	const handleEditAnchorDialogClose = useCallback(() => {
		setEditAnchorEntry(null);
	}, []);

	const handleEditAnchorDialogSubmit = useCallback(
		({ id, repositoryId, direction, hooks }: {
			id: string;
			repositoryId?: string | null;
			direction?: string | null;
			hooks: { id?: string; propertyId: string }[];
		}) => {
			const updatePayload = {
				id,
				repositoryId: repositoryId ?? null,
				direction: direction ?? null,
			};
			if (isAuthenticated) {
				entryUpdateMutation([updatePayload as Partial<Entry>]);
			} else {
				dispatch(entriesUpdateMany([updatePayload as Partial<Entry>]));
			}

			// Hooks always assign the anchor's currently-linked reference entry — recompute it on every save.
			const anchor = entryById.get(id) as AnchorEntry | undefined;
			const linkedReference = anchor?.referenceId
				? (visibleEntries as Entry[]).find((e) => e.id === anchor.referenceId) as ReferenceEntry | undefined
				: undefined;
			const value = linkedReference?.repositoryEntryId;
			const existingEffects = allAnchorEffects.filter((effect) => effect.anchorId === id);
			const keptIds = new Set(hooks.filter((hook) => hook.id).map((hook) => hook.id as string));
			const toDelete = existingEffects.filter((effect) => !keptIds.has(effect.id));

			if (repositoryId && value) {
				const toCreate = hooks.filter((hook) => !hook.id);
				const toKeep = hooks.filter((hook) => hook.id);
				if (toCreate.length) {
					const newEffects = toCreate.map((hook) => assignRandomId({
						type: EntityType.AnchorEffectAssignValue,
						parentId: parentId ?? null,
						anchorId: id,
						repositoryId,
						propertyId: hook.propertyId,
						value,
					}) as AnchorEffectAssignValueEntry);
					if (isAuthenticated) entryCreateMutation(newEffects);
					else dispatch(entriesAddMany(newEffects));
				}
				if (toKeep.length) {
					const keptUpdates = toKeep.map((hook) => ({ id: hook.id, propertyId: hook.propertyId, value }));
					if (isAuthenticated) entryUpdateMutation(keptUpdates as Partial<Entry>[]);
					else dispatch(entriesUpdateMany(keptUpdates));
				}
			}
			toDelete.forEach((effect) => {
				if (isAuthenticated) entryDeleteMutation({ id: effect.id });
				else dispatch(entriesDeleteMany([effect.id]));
			});

			setEditAnchorEntry(null);
		},
		[allAnchorEffects, dispatch, entryById, entryCreateMutation, entryDeleteMutation, entryUpdateMutation, isAuthenticated, parentId, visibleEntries]
	);

	const handleAddCanvasTimelineDialogClose = useCallback(() => {
		setIsAddTimelineDialogOpen(false);
	}, []);

	const handleAddCanvasTimelineDialogSubmit = useCallback(
		async ({ name, collections }: { name: string; collections: TimelineCollectionInput[] }) => {
			const xy = markerWorld ?? defaultPositionForIndex(visibleEntries.length);
			const timelineLocal = assignRandomId({
				type: EntityType.Timeline,
				parentId: parentId ?? null,
				name,
				xy,
			}) as TimelineEntry;
			setMarkerWorld(offsetPlacement(xy));
			setIsAddTimelineDialogOpen(false);
			const timeline = isAuthenticated
				? (await entryCreateMutation([timelineLocal]).unwrap())[0] as TimelineEntry
				: timelineLocal;
			const collectionEntries = collections.map(({ repositoryId, datePropertyId }) => assignRandomId({
				type: EntityType.TimelineCollection,
				parentId: parentId ?? null,
				timelineId: timeline.id,
				repositoryId,
				datePropertyId,
			}) as TimelineCollectionEntry);
			if (isAuthenticated) {
				entryCreateMutation(collectionEntries);
			} else {
				dispatch(entriesAddMany([timelineLocal, ...collectionEntries]));
			}
		},
		[dispatch, entryCreateMutation, isAuthenticated, markerWorld, parentId, visibleEntries.length]
	);

	const handleEditTimelineDialogClose = useCallback(() => {
		setEditTimelineEntry(null);
	}, []);

	const handleEditTimelineDialogSubmit = useCallback(
		({ id, name, collections }: { id: string; name: string; collections: TimelineCollectionInput[] }) => {
			const updatePayload = { id, name };
			if (isAuthenticated) {
				entryUpdateMutation([updatePayload as Partial<Entry>]);
			} else {
				dispatch(entriesUpdateMany([updatePayload as Partial<Entry>]));
			}

			const keptIds = new Set(collections.filter((collection) => collection.id).map((collection) => collection.id as string));
			const toDelete = allTimelineCollections.filter((collection) => collection.timelineId === id && !keptIds.has(collection.id));
			const toCreate = collections.filter((collection) => !collection.id).map(({ repositoryId, datePropertyId }) => assignRandomId({
				type: EntityType.TimelineCollection,
				parentId: parentId ?? null,
				timelineId: id,
				repositoryId,
				datePropertyId,
			}) as TimelineCollectionEntry);
			const toUpdate = collections
				.filter((collection) => collection.id)
				.map(({ id: collectionId, repositoryId, datePropertyId }) => ({ id: collectionId, repositoryId, datePropertyId }));
			if (toCreate.length) {
				if (isAuthenticated) entryCreateMutation(toCreate);
				else dispatch(entriesAddMany(toCreate));
			}
			if (toUpdate.length) {
				if (isAuthenticated) entryUpdateMutation(toUpdate as Partial<Entry>[]);
				else dispatch(entriesUpdateMany(toUpdate));
			}
			toDelete.forEach((collection) => {
				if (isAuthenticated) entryDeleteMutation({ id: collection.id });
				else dispatch(entriesDeleteMany([collection.id]));
			});

			setEditTimelineEntry(null);
		},
		[allTimelineCollections, dispatch, entryCreateMutation, entryDeleteMutation, entryUpdateMutation, isAuthenticated, parentId]
	);

	const handleAddCanvasChartDialogSubmit = useCallback(
		({ repositoryId, title, where, includeArchived, metric, xAxis, xAxisTimeScale, variant, targetA, targetB }: {
			repositoryId: string;
			title: string;
			where?: string;
			includeArchived: boolean;
			metric: string;
			xAxis: string;
			xAxisTimeScale?: 'D' | 'W' | 'M' | null;
			variant?: ChartPresentationVariant;
			targetA?: number | null;
			targetB?: number | null;
		}) => {
			const xy = markerWorld ?? defaultPositionForIndex(visibleEntries.length);
			const payload = assignRandomId({
				type: EntityType.Chart,
				parentId: parentId ?? null,
				repositoryId,
				rank: currentMaxRank + 1,
				title,
				where,
				includeArchived,
				metric,
				xAxis,
				xAxisTimeScale: xAxisTimeScale ?? null,
				variant: variant ?? 'line',
				targetA: targetA ?? null,
				targetB: targetB ?? null,
				xy,
			});
			if (isAuthenticated) {
				entryCreateMutation([payload as any]);
			} else {
				dispatch(entriesAddMany([payload as any]));
			}
			setMarkerWorld(offsetPlacement(xy));
			setIsAddChartDialogOpen(false);
		},
		[currentMaxRank, dispatch, entryCreateMutation, isAuthenticated, markerWorld, parentId, visibleEntries.length]
	);

	const handleEditChartDialogClose = useCallback(() => {
		setEditChartEntry(null);
	}, []);

	const updateChartEntry = useChartEntryUpdate();
	const handleEditChartDialogSubmit = useCallback<typeof updateChartEntry>(
		(payload) => {
			updateChartEntry(payload);
			setEditChartEntry(null);
		},
		[updateChartEntry]
	);

	const canvasAddElementSelectSlotInDialogs = canvasAddElementSelectSlot;
	// !isMdUp ? canvasAddElementSelectSlot : undefined;

	/**
	 * Backs `getOrCreateCardHandlers` below — see `buildCardHandlers` for why this exists. Reassigned (not
	 * mutated field-by-field) every render so it's always current by the time the card loop reads it.
	 */
	const cardInteractionDeps: CardInteractionDeps = {
		entryById,
		positionById,
		visibleEntries: visibleEntries as Entry[],
		activeGridEntry,
		handlePositionChange,
		syncAnchorAutoAssignFromPosition,
		getConstrainedDragPosition,
		constrainDragPositionForAnchors,
		getAnchorDragTilt,
		handleEntryFocus,
		handleEntryClick,
		handleEntryBlur,
		openListItemMenu,
		getIdsLinkedToGridAtAnchor,
		expandSelectedIdsWithSwimlaneChildren,
		swimlaneChildIdsByRootId,
		applyGridAxisPreviewDelta,
		applySwimlanePreviewDeltaForRoot,
	};
	const cardInteractionDepsRef = useRef(cardInteractionDeps);
	cardInteractionDepsRef.current = cardInteractionDeps;
	const cardInteractionStable: CardInteractionStable = {
		getPosition,
		setSelectedEntryIds,
		setDraggingReferenceRepositoryId,
		setIsDraggingNoteForAnchors,
		setDragSnapMode,
		setActiveSwimlaneCoord,
		dragSnapModeRef,
		selectedEntryIdsRef,
		groupDragStateRef,
		cardNodeByIdRef,
		connectorLineNodeRef,
		connectorLineIdsRef,
	};
	const cardHandlersCacheRef = useRef<Map<string, CardHandlerBundle>>(new Map());
	useEffect(() => {
		const liveIds = new Set((visibleEntries as Entry[]).map((e) => e.id));
		for (const id of Array.from(cardHandlersCacheRef.current.keys())) {
			if (!liveIds.has(id)) cardHandlersCacheRef.current.delete(id);
		}
	}, [visibleEntries]);
	const getOrCreateCardHandlers = (id: string): CardHandlerBundle => {
		const cached = cardHandlersCacheRef.current.get(id);
		if (cached) return cached;
		const handlers = buildCardHandlers(id, cardInteractionDepsRef, cardInteractionStable);
		cardHandlersCacheRef.current.set(id, handlers);
		return handlers;
	};

	if (!isContentLoaded) return null;

	return (
		<>
			{headerSlotEl && createPortal(
				<Box display='flex' alignItems='center' className={glassBarClass} sx={{ mr: 1 }}>
					<PositionLockButton isLocked={isPositionLocked} onToggle={togglePositionLock} />
				</Box>,
				headerSlotEl,
			)}
			<Box
				ref={containerRef}
				className="CanvasView"
				sx={{
					flex: 1,
					overflow: 'hidden',
					position: 'relative',
					touchAction: 'none',
					cursor: panStart ? 'grabbing' : isMarqueeActive ? 'crosshair' : 'default',
					...(scale >= CANVAS_DOT_GRID_MIN_SCALE
						? {
							backgroundImage: (theme) =>
								`radial-gradient(circle at center, ${theme.palette.divider} 1.25px, transparent 1.25px)`,
							backgroundRepeat: 'repeat',
						}
						: {
							backgroundImage: 'none',
							backgroundRepeat: 'no-repeat',
						}),
				}}
				style={
					scale >= CANVAS_DOT_GRID_MIN_SCALE
						? {
							backgroundSize: `${30 * scale}px ${30 * scale}px`,
							backgroundPosition: `calc(50% + ${pan.x}px) calc(50% + ${pan.y}px)`,
						}
						: undefined
				}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerCancel={handlePointerUp}
				onPointerLeave={handlePointerUp}
				onClick={handleCanvasClick}
			>
				<LockRipple ref={lockRippleRef} />
				{marqueeDisplayRect && (
					<Box
						aria-hidden
						sx={{
							position: 'absolute',
							left: marqueeDisplayRect.left,
							top: marqueeDisplayRect.top,
							width: marqueeDisplayRect.width,
							height: marqueeDisplayRect.height,
							boxSizing: 'border-box',
							border: (theme) => `1px solid ${theme.palette.primary.main}`,
							backgroundColor: (theme) => `${theme.palette.primary.main}22`,
							pointerEvents: 'none',
							zIndex: (theme) => theme.zIndex.modal,
						}}
					/>
				)}
				{!(isSmScreen && isAskAIBoxOpen) && (
					<CanvasScaleControl
						scale={scale}
						minScale={MIN_SCALE}
						maxScale={MAX_SCALE}
						onScaleChange={handleScaleControlChange}
					/>
				)}
				<Box
					data-canvas-world
					sx={{
						position: 'absolute',
						inset: 0,
						overflow: 'visible',
						// Scale around viewport center (matches click math: world at screen = center + pan + scale * world).
						transformOrigin: '50% 50%',
						pointerEvents: 'none',
					}}
					style={{
						transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
					}}
				>
					{markerWorld && (
						<CanvasClickMarker
							worldX={markerWorld.x}
							worldY={markerWorld.y}
							placementGeneration={markerPlacementGeneration}
							scale={scale}
						/>
					)}
					{typeof gridSnapX === 'number' && Boolean(activeGridAxisSnap?.yAxisRepositoryId) && Boolean(gridAxisBounds) && (
						<Box
							ref={verticalAxisNodeRef}
							sx={{
								position: 'absolute',
								left: '50%',
								top: '50%',
								height: (gridAxisBounds?.maxY ?? 0) - (gridAxisBounds?.minY ?? 0),
								borderLeft: (theme) => `${isDraggingYAxisReference ? 2 : 1}px dashed ${theme.palette.primary.main}`,
								opacity: isDraggingYAxisReference ? 0.9 : 0.45,
								pointerEvents: 'none',
								animation: isDraggingYAxisReference ? 'gridAxisPulse 0.9s ease-in-out infinite' : 'none',
								'@keyframes gridAxisPulse': {
									'0%': { opacity: 0.55 },
									'50%': { opacity: 1 },
									'100%': { opacity: 0.55 },
								},
							}}
							style={{
								transform: `translate(${gridSnapX}px, ${gridAxisBounds?.minY ?? 0}px)`,
							}}
						/>
					)}
					{typeof gridSnapY === 'number' && Boolean(activeGridAxisSnap?.xAxisRepositoryId) && Boolean(gridAxisBounds) && (
						<Box
							ref={horizontalAxisNodeRef}
							sx={{
								position: 'absolute',
								left: '50%',
								top: '50%',
								width: (gridAxisBounds?.maxX ?? 0) - (gridAxisBounds?.minX ?? 0),
								borderTop: (theme) => `${isDraggingXAxisReference ? 2 : 1}px dashed ${theme.palette.primary.main}`,
								opacity: isDraggingXAxisReference ? 0.9 : 0.45,
								pointerEvents: 'none',
								animation: isDraggingXAxisReference ? 'gridAxisPulseHorizontal 0.9s ease-in-out infinite' : 'none',
								'@keyframes gridAxisPulseHorizontal': {
									'0%': { opacity: 0.55 },
									'50%': { opacity: 1 },
									'100%': { opacity: 0.55 },
								},
							}}
							style={{
								transform: `translate(${gridAxisBounds?.minX ?? 0}px, ${gridSnapY}px)`,
							}}
						/>
					)}
					{projectedSwimlaneVertical.map((lane) => (
						<Box
							key={`swimlane-x-${lane.coord}`}
							ref={(el: HTMLDivElement | null) => {
								if (el) {
									projectedVerticalLaneNodeByCoordRef.current.set(String(lane.coord), el);
								} else {
									projectedVerticalLaneNodeByCoordRef.current.delete(String(lane.coord));
								}
							}}
							sx={{
								position: 'absolute',
								left: '50%',
								top: '50%',
								height: lane.max - lane.min,
								borderLeft: (theme) => `${isDraggingCollectionReference ? 2 : 1}px dashed ${theme.palette.info.main}`,
								opacity: isDraggingCollectionReference ? 0.9 : 0.4,
								pointerEvents: 'none',
								animation: isDraggingCollectionReference ? 'gridSwimlanePulseVertical 0.9s ease-in-out infinite' : 'none',
								'@keyframes gridSwimlanePulseVertical': {
									'0%': { opacity: 0.55 },
									'50%': { opacity: 1 },
									'100%': { opacity: 0.55 },
								},
							}}
							style={{
								transform: `translate(${lane.coord}px, ${lane.min}px)`,
							}}
						/>
					))}
					{projectedSwimlaneHorizontal.map((lane) => (
						<Box
							key={`swimlane-y-${lane.coord}`}
							ref={(el: HTMLDivElement | null) => {
								if (el) {
									projectedHorizontalLaneNodeByCoordRef.current.set(String(lane.coord), el);
								} else {
									projectedHorizontalLaneNodeByCoordRef.current.delete(String(lane.coord));
								}
							}}
							sx={{
								position: 'absolute',
								left: '50%',
								top: '50%',
								width: lane.max - lane.min,
								borderTop: (theme) => `${isDraggingCollectionReference ? 2 : 1}px dashed ${theme.palette.info.main}`,
								opacity: isDraggingCollectionReference ? 0.9 : 0.4,
								pointerEvents: 'none',
								animation: isDraggingCollectionReference ? 'gridSwimlanePulseHorizontal 0.9s ease-in-out infinite' : 'none',
								'@keyframes gridSwimlanePulseHorizontal': {
									'0%': { opacity: 0.55 },
									'50%': { opacity: 1 },
									'100%': { opacity: 0.55 },
								},
							}}
							style={{
								transform: `translate(${lane.min}px, ${lane.coord}px)`,
							}}
						/>
					))}
					{selectedPinConnection && (
						<CanvasConnectorLine connection={selectedPinConnection} nodeRef={connectorLineNodeRef} />
					)}
					{visibleEntries.map((entry, index) => {
						const position = getPosition(entry, index);
						const isSelected =
							selectedEntryIdsSet.has(entry.id) || selectedEntryId === entry.id || focusId === entry.id;
						const collectionColor =
							entry.type === EntityType.Reference
								? repositoryColorsById.get((entry as ReferenceEntry).repositoryId)
								: entry.type === EntityType.Query
									? repositoryColorsById.get((entry as QueryEntry).repositoryId)
									: entry.type === EntityType.Chart
										? repositoryColorsById.get((entry as ChartEntry).repositoryId)
										: undefined;
						const cardProps = {
							position,
							isSelected,
							scale,
							collectionColor,
							isPositionLocked,
							...getOrCreateCardHandlers(entry.id),
							setMenuAnchorEl,
						};
						return (
							<Box key={entry.id} data-canvas-card data-position-locked={cardProps.isPositionLocked || undefined} sx={{ pointerEvents: 'auto' }} onClick={(e) => e.stopPropagation()}>
								{entry.type === EntityType.Query && (
									<CanvasQueryCard
										{...cardProps}
										label={(entry as QueryEntry).title}
										repositoryId={(entry as QueryEntry).repositoryId}
										where={(entry as QueryEntry).where}
										includeArchived={(entry as QueryEntry).includeArchived !== false}
									/>
								)}
								{entry.type === EntityType.Chart && (
									<CanvasChartCard
										{...cardProps}
										repositoryId={(entry as ChartEntry).repositoryId}
										where={(entry as ChartEntry).where}
										includeArchived={(entry as ChartEntry).includeArchived !== false}
										metric={(entry as ChartEntry).metric}
										xAxis={(entry as ChartEntry).xAxis}
										xAxisTimeScale={(entry as ChartEntry).xAxisTimeScale}
										variant={(entry as ChartEntry).variant}
										targetA={(entry as ChartEntry).targetA}
										targetB={(entry as ChartEntry).targetB}
										label={(entry as ChartEntry).title}
									/>
								)}
								{entry.type === EntityType.Grid && (
									<CanvasGridCard
										{...cardProps}
									/>
								)}
								{entry.type === EntityType.Anchor && !(entry as AnchorEntry).referenceId && (
									<CanvasAnchorCard
										{...cardProps}
										direction={(entry as AnchorEntry).direction}
										lineSpec={anchorLineByAnchorId.get(entry.id)}
										repositoryTitle={allRepositories.find((r) => r.id === (entry as AnchorEntry).repositoryId)?.title ?? null}
									/>
								)}
								{entry.type === EntityType.Timeline && (
									<CanvasTimelineCard
										{...cardProps}
										timelineId={entry.id}
										name={(entry as TimelineEntry).name}
									/>
								)}
								{entry.type === EntityType.Reference && !isReferencesAtLocationBundleLoadFetching && (() => {
									const ref = entry as ReferenceEntry;
									const attachedAnchorLines = anchorLinesByReferenceId.get(entry.id) ?? EMPTY_ANCHOR_LINES;
									const referenceZIndex = attachedAnchorLines.length > 0
										? REFERENCE_CARD_HOSTING_ANCHOR_Z_INDEX
										: REFERENCE_CARD_Z_INDEX;
									return (
										<CanvasReferenceCard
											{...cardProps}
											repositoryId={ref.repositoryId}
											repositoryEntryId={ref.repositoryEntryId}
											attachedAnchorLines={attachedAnchorLines}
											zIndex={referenceZIndex}
										/>
									);
								})()}
								{entry.type === EntityType.Note && (
									<CanvasNoteCard
										{...cardProps}
										isBundleLoading={isNotesAtLocationBundleLoadFetching}
										noteId={entry.id}
										label={(entry as NoteEntry).title}
										variant={(entry as NoteEntry).variant ?? 'default'}
										color={(entry as NoteEntry).color}
										isDraft={(entry as NoteEntry).isDraft}
									/>
								)}
							</Box>
						);
					})}
				</Box>
			</Box>
			<ChatFlow
				open={isAskAIBoxOpen || isChatWindowOpen}
				mode={isWelcomeMode ? 'welcome' : 'docked'}
				isChatOpen={isChatWindowOpen}
				onClose={hideAskAIBox}
				onCloseChat={() => setIsChatWindowOpen(false)}
				isAuthenticated={isAuthenticated}
				onOpenConversation={handleOpenPastConversation}
				chatDraft={chatDraft}
				setChatDraft={setChatDraft}
				setIsAiSearchFocused={setIsAiSearchFocused}
				isAskAiInProgress={!USE_MOCK_AI_RESPONSE && isAskAiInProgress}
				handleChatSubmit={handleChatSubmit}
				messages={chatMessages}
			/>
			{menuEntry && <EntryMenu
				options={[]}
				anchorEl={menuAnchorEl}
				onClose={closeListItemMenu}
				item={menuEntry}
			>
				<MenuItem onClick={() => handleCanvasMenuGoToClick(menuEntry)}>
					<RowMenuOptionGoTo keyboardShortcut />
				</MenuItem>
				{menuEntry.type !== EntityType.Reference && (
					<MenuItem onClick={() => handleMenuEditBtnClick(menuEntry.id)}>
						<RowMenuOptionEdit keyboardShortcut />
					</MenuItem>
				)}
				<MenuItem onClick={handleCanvasMenuDeleteClick}>
					<RowMenuOptionDelete keyboardShortcut />
				</MenuItem>
				<MenuItem onClick={handleCanvasMenuCutClick}>
					<RowMenuOptionClip keyboardShortcut />
				</MenuItem>
			</EntryMenu>}
			<AddNoteDialog
				open={isAddDialogOpen}
				onClose={closeAddDialog}
				elementSelectSlot={canvasAddElementSelectSlotInDialogs}
				onSubmit={(formData: FormDataType) => {
					const xy = markerWorld ?? defaultPositionForIndex(visibleEntries.length);
					const newEntryPayload = assignRandomId({
						...formData,
						parentId: parentId ?? null,
						rank: currentMaxRank + 1,
						xy,
					});
					if (isAuthenticated) {
						createNoteMutation(newEntryPayload as NoteEntry);
					} else {
						dispatch(entriesAddMany([newEntryPayload as any]));
					}
					setMarkerWorld(offsetPlacement(xy));
					closeAddDialog();
				}}
			/>
			<AddQueryDialog
				open={isAddQueryDialogOpen}
				repositories={addQueryRepositories}
				onClose={handleAddCanvasQueryDialogClose}
				onSubmit={handleAddCanvasQueryDialogSubmit}
				elementSelectSlot={canvasAddElementSelectSlotInDialogs}
			/>
			<EditQueryDialog
				open={isEditQueryDialogOpen}
				query={editQueryEntry}
				repositories={addQueryRepositories}
				onClose={handleEditQueryDialogClose}
				onSubmit={handleEditQueryDialogSubmit}
			/>
			<EditNoteDialog
				open={isEditNoteDialogOpen}
				note={editNoteEntry}
				onClose={handleEditNoteDialogClose}
				onAutosave={handleEditNoteDialogSave}
				onSaveAndClose={handleEditNoteDialogSaveAndClose}
			/>
			<AddChartDialog
				open={isAddChartDialogOpen}
				repositories={addQueryRepositories}
				onClose={handleAddCanvasChartDialogClose}
				onSubmit={handleAddCanvasChartDialogSubmit}
				elementSelectSlot={canvasAddElementSelectSlotInDialogs}
			/>
			<AddAnchorDialog
				open={isAddGridDialogOpen}
				repositories={addQueryRepositories}
				onClose={handleAddCanvasGridDialogClose}
				onSubmit={handleAddCanvasGridDialogSubmit}
				elementSelectSlot={canvasAddElementSelectSlotInDialogs}
			/>
			<EditAnchorDialog
				open={isEditAnchorDialogOpen}
				anchor={editAnchorEntry}
				repositories={addQueryRepositories}
				anchorReferenceRepositoryId={editAnchorEntry?.referenceId
					? ((visibleEntries as Entry[]).find((e) => e.id === editAnchorEntry.referenceId) as ReferenceEntry | undefined)?.repositoryId ?? null
					: null}
				onClose={handleEditAnchorDialogClose}
				onSubmit={handleEditAnchorDialogSubmit}
			/>
			<AddTimelineDialog
				open={isAddTimelineDialogOpen}
				repositories={addQueryRepositories}
				onClose={handleAddCanvasTimelineDialogClose}
				onSubmit={handleAddCanvasTimelineDialogSubmit}
				elementSelectSlot={canvasAddElementSelectSlotInDialogs}
			/>
			<EditTimelineDialog
				open={isEditTimelineDialogOpen}
				timeline={editTimelineEntry}
				repositories={addQueryRepositories}
				onClose={handleEditTimelineDialogClose}
				onSubmit={handleEditTimelineDialogSubmit}
			/>
			<EditGridDialog
				open={isEditGridDialogOpen}
				grid={editGridEntry}
				repositories={addQueryRepositories}
				onClose={handleEditGridDialogClose}
				onSubmit={handleEditGridDialogSubmit}
			/>
			<EditChartDialog
				open={isEditChartDialogOpen}
				chart={editChartEntry}
				repositories={addQueryRepositories}
				onClose={handleEditChartDialogClose}
				onSubmit={handleEditChartDialogSubmit}
			/>
			<AddRepositoryEntryFormDialog
				open={isAddRepositoryEntryDialogOpen}
				repositoryId={addRepositoryEntryDialog?.repositoryId ?? null}
				linkToProperty={addRepositoryEntryDialog?.linkToProperty}
				linkToEntryId={addRepositoryEntryDialog?.linkToEntryId}
				onClose={repositoryEntryCanvasEditFlow.closeAddRepositoryEntryDialogWithCleanup}
				onCreated={(created) => {
					if (!repositoryEntryCanvasEditFlow.notifyAddRepositoryEntryCreated(created)) {
						handleRepositoryEntryCreatedFromCanvas(created);
					}
				}}
				title='Add entry'
				entryPicker={{
					isEntryPickable: isPickerEntryNotOnCanvas,
					describePickableCount: (count, repositoryTitle) => (count
						? `${count} existing entries are not part of this board. Pick from them?`
						: `All entries from ${repositoryTitle} are on this board`),
					hint: 'Tap on entries you want to add to the board.',
					confirmLabel: (count) => `Add ${count}`,
					onConfirm: handleReferencePickerConfirm,
				}}
				elementSelectSlot={canvasAddElementSelectSlotInDialogs}
			/>
			<RepositoryEntryDrawer
				open={repositoryEntryDrawerOpen}
				onClose={() => requestRepositoryEntryDrawer(null)}
				repositoryEntryId={repositoryEntryDrawer?.repositoryEntryId ?? null}
				editFlow={repositoryEntryCanvasEditFlow}
				drawerChanges={repositoryEntryDrawerChanges}
				populatedEntry={repositoryEntryDocumentData.populatedEntry}
				repository={repositoryEntryDocumentData.repository}
				propertyDefinitions={repositoryEntryDocumentData.propertyDefinitions}
				isLoading={repositoryEntryDocumentData.isLoading}
				attachedAnchors={drawerAttachedAnchors}
				anchorTargetRepositoryTitleByAnchorId={drawerAnchorTargetRepositoryTitleByAnchorId}
				onEditAnchor={(anchor) => setEditAnchorEntry(anchor)}
				onUnpinAnchor={(anchor) => handleUnpinEntry(anchor)}
			/>
			<EditFormDialog
				open={isEditDialogOpen}
				handleClose={closeEditDialog}
				handleSubmit={onEditFormSubmit}
				title={editDialogTitle}
			/>
			{/* </EditFormDialog> */}
			<ConfirmDeleteEntryDialog
				open={isConfirmDeleteDialogOpen}
				handleClose={closeConfirmDeleteDialog}
				handleSubmit={() => {
					handleEntryBlur();
					submitConfirmDeleteDialog({
						redirectPath:
							confirmDeleteDialogEntry?.parentId != null
								? `/${confirmDeleteDialogEntry.parentId}`
								: undefined,
					});
				}}
				title={confirmDeleteDialogTitle}
				entry={confirmDeleteDialogEntry}
			/>
			<ConfirmDeleteEntriesDialogV2
				open={Boolean(pendingBulkDeleteIds?.length)}
				count={pendingBulkDeleteIds?.length ?? 0}
				onClose={() => setPendingBulkDeleteIds(null)}
				onConfirm={handleConfirmBulkDelete}
			/>
			{!(isSmScreen && isAskAIBoxOpen) && <FabAreaWrapperBottomRight>
				<Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap={2}>
					<ClipboardActionsFab markerWorld={markerWorld} />
					{isAuthenticated && localRecordsToSync.length > 0 && !selectedEntryId && (
						<Tooltip title='Sync offline data to cloud' disableInteractive>
							<IconButton
								color='warning'
								onClick={() => setIsSyncDialogOpen(true)}
								sx={{color: 'text.secondary', '&:hover': {color: 'warning.main'}}}
							>
								<SyncProblemIcon />
							</IconButton>
						</Tooltip>
					)}
					<EntryExternalLinkActions urls={selectedEntryExternalUrls} />
					{selectedPinAction && (
						<Tooltip title={selectedPinAction.isPinned ? 'Unpin' : 'Pin to nearest reference'} disableInteractive>
							<ActionAreaBtn size='large' onClick={() => {
								if (selectedPinAction.isPinned) handleUnpinEntry(selectedPinAction.entry);
								else handlePinEntryToNearestReference(selectedPinAction.entry);
							}}>
								{selectedPinAction.isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
							</ActionAreaBtn>
						</Tooltip>
					)}
					{selectedEntryId && entryById.get(selectedEntryId)?.type === EntityType.Reference && (
						<Tooltip title='Edit' disableInteractive>
							<ActionAreaBtn size='large' onClick={() => handleMenuEditBtnClick(selectedEntryId)}>
								<EditIcon />
							</ActionAreaBtn>
						</Tooltip>
					)}
					{selectedEntryId && <ActionAreaBtn size='large' onClick={(e) => {
						setMenuAnchorEl(e.currentTarget);
						openListItemMenu(visibleEntries.find((entry) => entry.id === selectedEntryId) as Entry);
					}}>
						<MoreVertIcon />
					</ActionAreaBtn>}
					{!selectedEntryId && !isAskAIBoxOpen && (
						<Tooltip title={!isAuthenticated ? SIGN_IN_TOOLTIP_TEXT : ''} disableInteractive>
							<ActionAreaBtn
								disabled={Boolean(selectedEntryId)}
								onClick={isAuthenticated ? showAskAIBox : undefined}
								aria-label='Ask AI'
							>
								<AssistantIcon color={isAuthenticated && !selectedEntryId ? 'primary' : 'disabled'} />
							</ActionAreaBtn>
						</Tooltip>
					)}
					{selectedEntryId && <ActionAreaFab size='large' autoFocus
						component={Link}
						to={selectedEntryUrl ?? ''}
					>
						<ArrowForwardIcon color='inherit' />
					</ActionAreaFab>}
					{!selectedEntryId && <ActionAreaFab size='large' onClick={handleAddEntryClick} disabled={!isContentLoaded || Boolean(selectedEntryId)}>
						<AddIcon />
					</ActionAreaFab>}
				</Box>
			</FabAreaWrapperBottomRight>}
			<SyncOfflineDataDialog
				open={isSyncDialogOpen}
				records={localRecordsToSync}
				onClose={() => setIsSyncDialogOpen(false)}
				onNotNow={handleDismissLocalRecordsSync}
				onSynced={handleLocalRecordsSynced}
			/>
			<Snackbar
				open={isSyncDismissedSnackbarOpen}
				autoHideDuration={6000}
				onClose={() => setIsSyncDismissedSnackbarOpen(false)}
				message='You can sync your offline work anytime from Settings.'
			/>
		</>
	);
}

export default CanvasView;
